import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useOutletContext, Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Loader2,
  Mail,
  Phone,
  Globe,
  MapPin,
  User,
  Briefcase,
  GraduationCap,
  Activity,
  Award,
  CalendarDays,
  IndianRupee,
  Clock,
  FileText,
  Calendar as CalendarIcon,
  Edit,
  ChevronDown,
  X,
  RotateCcw,
  Sparkles,
  Check,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Zap,
  Plus,
  Download,
  ChevronRight,
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import { Button } from 'src/components/ui/button';
import { useResumeUpload } from 'src/hooks/useResumeUpload';
import { Input } from 'src/components/ui/input';
import { Textarea } from 'src/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { Calendar } from 'src/components/ui/calendar';
import { format, subYears } from 'date-fns';
import { cn } from 'src/lib/utils';
import { FormField } from 'src/components/common/FormField';
import { MultiSelectDropdown } from 'src/components/common/MultiSelectDropdown';
import {
  REFERENCE_DATA_QUERY_OPTIONS,
  useProtectedResumeProfile,
  useReferenceData,
  useUpdateProfileSection,
  useUpdateResumeSection,
  useSaveWorkExperiences,
  useResumePdf,
} from 'src/hooks/useResumeData';
import {
  validateResumeForm,
  validateRequiredQualifications,
  validateDuplicateSchoolQualifications,
  validatePreferences,
  getEducationAttentionToastMessage,
} from 'src/utils/resume-validation';
import {
  isCertificationsFormEmpty,
  isCertificationsSectionComplete,
} from 'src/utils/certificationsForm';
import { getUser } from 'src/utils/authStorage';
import { buildResumeUploadProgressMeta } from 'src/utils/resumeUploadProgressMeta';
import {
  formatIndianNumber,
  convertToLPA,
  convertFromLPA,
} from 'src/utils/numberFormat';
import { formatRelativeTime, formatSize } from 'src/utils/formatters';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from 'src/components/ui/tooltip';
import {
  defaultValues,
  getEmptyEducation,
  getEmptyWorkExperience,
  filterNonBlankEducations,
  isBlankEducationEntry,
  mapWorkExperiencesWithDuties,
} from '../../components/resume/constants';
import {
  diffWorkExperiences,
  collectDutyFlushOps,
} from '../../components/resume/workExperienceSync';
import { PersonalInfoSection } from '../../components/resume/PersonalInfoSection';
import { EducationAccordionForm } from '../../components/resume/EducationAccordionForm';
import {
  WorkExperienceFields,
  AddExperienceButton,
} from '../../components/resume/WorkExperienceFields';
import { normalizeWorkExperienceDuties } from '../../components/resume/WorkExperienceAccordionDisplay';
import { SkillsSection } from '../../components/resume/SkillsSection';
import { CertificationsSection } from '../../components/resume/CertificationsSection';
import createResumeApi from '../../components/resume/resumeApi';
import { ResumeService } from '../../services/api/ResumeService';
import { useUnconfirmedSkills } from '../../hooks/useSkillsData';
import { OTPInput } from '../../components/OTPVerification/OTPInput';
import RoleInlineEdit from './RoleInlineEdit';
import { ResumeProcessingOverlay } from './ResumeProcessingOverlay';
import './ResumeDashboard.css';

// Processing overlay constants
const UPLOAD_PROCESSING_POLL_INTERVAL_MS = 5000; // Poll every 5s for upload_processing
/** First poll when processing is detected without a dashboard upload (bootstrap, signup → dashboard, etc.). */
const UPLOAD_PROCESSING_FIRST_POLL_DELAY_MS = 5000;
/** First poll after user uploads from this dashboard (slightly earlier than default). */
const UPLOAD_PROCESSING_FIRST_POLL_DELAY_AFTER_DASHBOARD_UPLOAD_MS = 4000;

const randomIntInclusive = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;
const getProcessingStartPercent = () => randomIntInclusive(2, 5);
const getProcessingPollIncrement = () => randomIntInclusive(7, 12);

const MONTH_ABBREVIATIONS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatMonthFromNumeric = (monthValue) => {
  if (monthValue === undefined || monthValue === null) return '';
  const numericMonth =
    typeof monthValue === 'string' ? parseInt(monthValue, 10) : monthValue;
  if (Number.isNaN(numericMonth) || numericMonth < 1 || numericMonth > 12) {
    return String(monthValue);
  }
  return MONTH_ABBREVIATIONS[numericMonth - 1];
};

const formatDateHuman = (input) => {
  if (!input) return '';
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const [y, m, d] = input.split('-').map((v) => parseInt(v, 10));
    const date = new Date(y, m - 1, d);
    const day = date.getDate();
    const month = MONTH_ABBREVIATIONS[date.getMonth()].toLowerCase();
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return String(input);
  const day = date.getDate();
  const month = MONTH_ABBREVIATIONS[date.getMonth()].toLowerCase();
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const formatPhone = (number) => {
  if (!number) return '';
  const digits = String(number).replace(/\D/g, '');
  if (digits.length === 10)
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  return `+91 ${digits}`;
};

/** Max CTC in LPA for preference form inputs (stored value = LPA × 1e5) */
const CTC_MAX_LPA = 99;
const CTC_MAX_AMOUNT = CTC_MAX_LPA * 100000;

/** Quick-pick pills; custom days can be typed in the adjacent input */
const NOTICE_PERIOD_OPTIONS = [
  { value: 0, label: 'Immediate' },
  { value: 30, label: '30' },
  { value: 60, label: '60' },
  { value: 90, label: '90+' },
];

const WORK_MODE_OPTIONS = [
  { value: 'ONSITE', label: 'Onsite' },
  { value: 'HYBRID', label: 'Hybrid' },
  { value: 'REMOTE', label: 'Remote' },
  { value: 'FLEXIBLE', label: 'Flexible' },
];

const COMPANY_SIZE_OPTIONS = [
  { value: 'STARTUP', label: 'Startup', hint: '<50' },
  { value: 'SME', label: 'SME', hint: '50–250' },
  { value: 'MID', label: 'Mid-size', hint: '250–1K' },
  { value: 'LARGE', label: 'Large', hint: '1K–5K' },
  { value: 'ENTERPRISE', label: 'Enterprise', hint: '5K+' },
  { value: 'NO_PREFERENCE', label: 'No preference', hint: '' },
];

const PROFICIENCY_LEVELS = [
  { value: 'NATIVE', label: 'Native', hint: 'Mother tongue or native fluency' },
  { value: 'FLUENT', label: 'Fluent', hint: 'Comfortable in professional use' },
  {
    value: 'CONVERSATIONAL',
    label: 'Conversational',
    hint: 'Can hold day-to-day conversations',
  },
  { value: 'BASIC', label: 'Basic', hint: 'Limited working knowledge' },
];

function proficiencyLabel(v) {
  const o = PROFICIENCY_LEVELS.find((p) => p.value === v);
  return o ? o.label : v || '';
}

function validateNoticePeriodValue(value) {
  if (value !== 0 && (value === null || value === undefined || value === '')) {
    return 'Notice period is required';
  }
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (Number.isNaN(n) || !Number.isFinite(n) || n < 0) {
    return 'Enter a valid number of days (0 or more)';
  }
  return true;
}

function formatNoticePeriodBadge(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  if (n === 0) return 'Immediate';
  if (n === 90) return '90+';
  return `${n} days`;
}

function hasNoticePeriodValue(value) {
  return (
    value !== null &&
    value !== undefined &&
    value !== '' &&
    !Number.isNaN(Number(value)) &&
    Number(value) >= 0
  );
}

// Helper function to determine if summary length is "great" (200-1000 characters)
const isSummaryLengthGreat = (summary) => {
  if (!summary) return false;
  const { length } = summary;
  return length >= 200 && length <= 1000;
};

// Helper function to validate if a work experience entry is complete
// An entry is complete if it has non-null values for critical fields
const isWorkExperienceEntryComplete = (exp) => {
  if (!exp) return false;

  // Check if company/company_name is present (non-null, non-empty)
  const company = exp.company || exp.company_name;
  const hasCompany = !!(company && company !== null && company !== '');

  // Check if role is present (non-null, non-empty)
  const hasRole = !!(exp.role && exp.role !== null && exp.role !== '');

  // Check if start date is present (either from_date string or employment_period object)
  const hasStartDate = !!(
    (exp.from_date && exp.from_date !== null && exp.from_date !== '') ||
    (exp.employment_period?.startMonth && exp.employment_period?.startYear)
  );

  // If it's a current job (is_current or present), we don't need end date
  const isCurrent = !!(exp.is_current || exp.employment_period?.present);

  // Check if end date is present (only required if not current)
  const hasEndDate =
    isCurrent ||
    !!(
      (exp.to_date && exp.to_date !== null && exp.to_date !== '') ||
      (exp.employment_period?.endMonth && exp.employment_period?.endYear)
    );

  // Entry is complete if it has company, role, start date, and end date (if not current)
  return hasCompany && hasRole && hasStartDate && hasEndDate;
};

// Helper function to check if all work experiences are complete
const areWorkExperiencesComplete = (workExperiences, noWorkExp) => {
  // If marked as fresher (no work exp), it's complete
  if (noWorkExp) return true;

  // If no work experiences array or empty, it's incomplete
  if (!workExperiences || workExperiences.length === 0) return false;

  // Check if all entries are complete
  // Filter out completely empty entries first
  const validEntries = workExperiences.filter(
    (exp) =>
      exp.company ||
      exp.company_name ||
      exp.role ||
      exp.from_date ||
      exp.employment_period
  );

  // If no valid entries, it's incomplete
  if (validEntries.length === 0) return false;

  // All valid entries must be complete
  return validEntries.every(isWorkExperienceEntryComplete);
};

const hasNonEmpty = (v) => {
  if (v === undefined || v === null) return false;
  return String(v).trim().length > 0;
};

const normalizeEducationQualification = (qualification) =>
  String(qualification || '')
    .trim()
    .toUpperCase();

const isSchoolEducationQualification = (qualification) => {
  const q = normalizeEducationQualification(qualification);
  return (
    q === 'TENTH' ||
    q === 'SECONDARY' ||
    q === 'TWELFTH' ||
    q === 'HIGHER_SECONDARY'
  );
};

const isTenthEducationQualification = (qualification) => {
  const q = normalizeEducationQualification(qualification);
  return q === 'TENTH' || q === 'SECONDARY';
};

const isHigherSecondaryEducationQualification = (qualification) => {
  const q = normalizeEducationQualification(qualification);
  return q === 'TWELFTH' || q === 'HIGHER_SECONDARY';
};

const isDegreeEducationQualification = (qualification) => {
  const q = normalizeEducationQualification(qualification);
  return (
    q === 'DEGREE' ||
    q === 'MASTERS' ||
    q === "BACHELOR'S DEGREE" ||
    q === "MASTER'S DEGREE"
  );
};

const hasAnyEducationData = (educations) =>
  Array.isArray(educations) &&
  educations.some(
    (edu) =>
      hasNonEmpty(edu?.institution) ||
      hasNonEmpty(edu?.qualification) ||
      hasNonEmpty(edu?.graduationYear) ||
      hasNonEmpty(edu?.board) ||
      hasNonEmpty(edu?.university) ||
      hasNonEmpty(edu?.fieldsOfStudy) ||
      hasNonEmpty(edu?.fieldOfStudyVal) ||
      hasNonEmpty(edu?.maxMarks) ||
      hasNonEmpty(edu?.earnedMarks) ||
      hasNonEmpty(edu?.maxGPA) ||
      hasNonEmpty(edu?.earnedGPA)
  );

const isEducationEntryComplete = (edu) => {
  if (!edu) return false;

  const qualification = normalizeEducationQualification(edu.qualification);
  if (!hasNonEmpty(qualification)) return false;
  if (!hasNonEmpty(edu.institution)) return false;
  if (!hasNonEmpty(edu.graduationYear)) return false;

  if (isSchoolEducationQualification(qualification) && !hasNonEmpty(edu.board))
    return false;
  if (
    !isTenthEducationQualification(qualification) &&
    !(hasNonEmpty(edu.fieldsOfStudy) || hasNonEmpty(edu.fieldOfStudyVal))
  ) {
    return false;
  }
  if (
    isDegreeEducationQualification(qualification) &&
    !hasNonEmpty(edu.university)
  )
    return false;

  const gradeType = String(edu.gradeType || '')
    .trim()
    .toUpperCase();
  const hasMarksPair =
    hasNonEmpty(edu.maxMarks) && hasNonEmpty(edu.earnedMarks);
  const hasGPAPair = hasNonEmpty(edu.maxGPA) && hasNonEmpty(edu.earnedGPA);
  if (gradeType === 'GPA' || gradeType === 'CGPA') return hasGPAPair;
  if (gradeType === 'MARKS') return hasMarksPair;
  return hasMarksPair || hasGPAPair;
};

const areEducationsComplete = (educations) => {
  if (!Array.isArray(educations) || educations.length === 0) return false;
  const validEntries = educations.filter(
    (edu) =>
      hasNonEmpty(edu?.institution) ||
      hasNonEmpty(edu?.qualification) ||
      hasNonEmpty(edu?.graduationYear) ||
      hasNonEmpty(edu?.board) ||
      hasNonEmpty(edu?.university) ||
      hasNonEmpty(edu?.fieldsOfStudy) ||
      hasNonEmpty(edu?.fieldOfStudyVal) ||
      hasNonEmpty(edu?.maxMarks) ||
      hasNonEmpty(edu?.earnedMarks) ||
      hasNonEmpty(edu?.maxGPA) ||
      hasNonEmpty(edu?.earnedGPA)
  );
  if (validEntries.length === 0) return false;

  // Education section is considered complete only when:
  // - all non-empty entries are individually complete, AND
  // - user has provided Secondary + Higher Secondary + at least one Degree (Bachelor/Master)
  if (!validEntries.every(isEducationEntryComplete)) return false;

  const completedEntries = validEntries; // all are complete due to check above
  const hasSecondary = completedEntries.some((e) =>
    isTenthEducationQualification(e?.qualification)
  );
  const hasHigherSecondary = completedEntries.some((e) =>
    isHigherSecondaryEducationQualification(e?.qualification)
  );
  const hasDegree = completedEntries.some((e) =>
    isDegreeEducationQualification(e?.qualification)
  );

  return hasSecondary && hasHigherSecondary && hasDegree;
};

// Professional Summary visibility: only whether each tier has a qualification set
// (no institution/year/board/GPA checks — unlike areEducationsComplete).
const hasRequiredEducationQualificationsForSummary = (educations) => {
  if (!Array.isArray(educations) || educations.length === 0) return false;
  const hasSecondary = educations.some(
    (e) =>
      hasNonEmpty(e?.qualification) &&
      isTenthEducationQualification(e.qualification)
  );
  const hasHigherSecondary = educations.some(
    (e) =>
      hasNonEmpty(e?.qualification) &&
      isHigherSecondaryEducationQualification(e.qualification)
  );
  const hasDegree = educations.some(
    (e) =>
      hasNonEmpty(e?.qualification) &&
      isDegreeEducationQualification(e.qualification)
  );
  return hasSecondary && hasHigherSecondary && hasDegree;
};

// Calculate work experience duration
const calculateDuration = (
  startMonth,
  startYear,
  endMonth,
  endYear,
  isPresent
) => {
  if (!startMonth || !startYear) return null;

  const start = new Date(
    parseInt(startYear, 10),
    parseInt(startMonth, 10) - 1,
    1
  );
  const end = isPresent
    ? new Date()
    : new Date(parseInt(endYear, 10), parseInt(endMonth, 10) - 1, 1);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

  let months =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());

  // If same month, count as at least 1 month
  if (months === 0) months = 1;

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years === 0 && remainingMonths === 0) return null;

  let duration = '';
  if (years > 0) {
    duration += `${years} ${years === 1 ? 'yr' : 'yr'}`;
  }
  if (remainingMonths > 0) {
    if (duration) duration += ' ';
    duration += `${remainingMonths} ${remainingMonths === 1 ? 'mo' : 'mo'}`;
  }

  return duration;
};

// Convert DD/MM/YYYY to YYYY-MM-DD for date input
const convertToDateInputFormat = (dateString) => {
  if (!dateString) return '';
  // Check if already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  // Check if in DD/MM/YYYY format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  }
  // Try to parse as Date and convert
  const date = new Date(dateString);
  if (!Number.isNaN(date.getTime())) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return '';
};

// DatePicker component for Date of Birth
const DatePickerWithYear = ({ field, error }) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const currentYear = new Date().getFullYear();

  // Parse the date string in DD/MM/YYYY format
  const parseDate = (dateString) => {
    if (!dateString) return null;
    // Check if in DD/MM/YYYY format
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('/');
      return new Date(year, month - 1, day); // month is 0-based
    }
    // Check if in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return new Date(year, month - 1, day);
    }
    return null;
  };

  // Format date to DD/MM/YYYY
  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'dd/MM/yyyy');
  };

  const selectedDate = field.value ? parseDate(field.value) : undefined;
  const defaultMonth = selectedDate || new Date(currentYear - 18, 0, 1);

  const today = new Date();
  const hundredYearsAgo = subYears(today, 100);
  const eighteenYearsAgo = subYears(today, 18);

  const handleSelect = (date) => {
    if (date) {
      field.onChange(formatDate(date)); // Store date as DD/MM/YYYY string
      setCalendarOpen(false);
    }
  };

  const errorClass = error
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
    : '';

  return (
    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
      <PopoverTrigger asChild>
        <Button
          data-field="dob"
          variant="outline"
          className={cn(
            'w-full justify-between text-left font-normal h-10',
            !selectedDate && 'text-muted-foreground',
            errorClass
          )}
        >
          <span className="truncate">
            {selectedDate
              ? format(selectedDate, 'dd/MM/yyyy')
              : 'Pick your date of birth'}
          </span>
          <CalendarIcon className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          disabled={
            (date) =>
              date > eighteenYearsAgo || // Cannot select dates less than 18 years ago
              date < hundredYearsAgo // Cannot select dates more than 100 years ago
          }
          initialFocus
          defaultMonth={defaultMonth}
          fromYear={currentYear - 100} // Earliest selectable year
          toYear={currentYear - 18} // Latest selectable year
          captionLayout="dropdown-buttons"
          classNames={{
            caption_label: 'hidden',
            dropdown_month: 'flex justify-center text-sm font-medium',
            dropdown_year: 'flex justify-center text-sm font-medium',
            dropdown_icon: 'hidden',
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

// Convert YYYY-MM-DD back to DD/MM/YYYY for API
const convertFromDateInputFormat = (dateString) => {
  if (!dateString) return '';
  // Check if in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }
  // If already in DD/MM/YYYY, return as is
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    return dateString;
  }
  return dateString;
};

// Accordion list for work experience – only one card open at a time
const ExperienceAccordionList = ({
  experiences,
  control,
  watch,
  setValue,
  getValues,
  errors,
  getEmptyWorkExperience,
  initialExpandedIndex = null,
  validationActive = false,
  validationKey = 0,
}) => {
  const entryCardRefs = useRef([]);
  const lastAutoFocusKeyRef = useRef(0);

  // UX parity with Education: when opening the edit sheet, keep all entries collapsed by default.
  const [expandedIndex, setExpandedIndex] = React.useState(
    initialExpandedIndex !== null ? initialExpandedIndex : null
  );

  const handleToggle = (index, open) => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[experience-accordion] toggle', {
        index,
        open,
        prevExpandedIndex: expandedIndex,
      });
    }
    setExpandedIndex(open ? index : null);
  };

  const hasText = (val) => String(val || '').trim().length > 0;
  const getPeriodIssueLabel = (msg) => {
    const m = String(msg || '').toLowerCase();
    if (!m) return 'Invalid period';
    if (m.includes('overlap')) return 'Overlapping period';
    if (m.includes('only one current')) return 'Multiple current jobs';
    if (m.includes('current job must be the latest'))
      return 'Current job must be latest';
    if (m.includes('from date')) return 'From date';
    if (m.includes('to date')) return 'To date';
    return 'Invalid period';
  };

  const getMissingLabelsForExperience = (exp = {}, expError = null) => {
    const labels = [];
    const period = exp.employment_period || {};
    const isCurrent = !!(exp.is_current || period.present);

    const hasAnyValue =
      hasText(exp.role) ||
      hasText(exp.company) ||
      !!period.startMonth ||
      !!period.startYear ||
      !!period.endMonth ||
      !!period.endYear ||
      !!period.present;

    if (!hasAnyValue) return labels;

    if (!hasText(exp.role)) labels.push('Job Title');
    if (!hasText(exp.company)) labels.push('Company');

    const hasAllStart = !!(period.startMonth && period.startYear);
    if (!hasAllStart) labels.push('From date');

    const hasEnd = !!(period.endMonth && period.endYear);
    if (!isCurrent && !hasEnd) labels.push('To date');

    // Include cross-entry/cross-field period validations (overlap, multiple current, current-not-latest, etc.)
    const periodErr = expError?.employment_period;
    const periodMsg =
      periodErr?.message ||
      (typeof periodErr === 'string' ? periodErr : '') ||
      '';
    if (periodErr) {
      const label = getPeriodIssueLabel(periodMsg);
      if (!labels.includes(label)) labels.push(label);
    }

    return labels;
  };

  // NOTE: Avoid memoizing purely on array reference.
  // In some RHF scenarios, nested edits update values without changing the
  // `workExperiences` array identity, which caused the banner to miss rows.
  const errorEntryMeta = validationActive
    ? (experiences || [])
        .map((exp, idx) => {
          const expError = errors?.workExperiences?.[idx] || null;
          const missingLabels = getMissingLabelsForExperience(
            exp || {},
            expError
          );
          return { idx, missingLabels, missingCount: missingLabels.length };
        })
        .filter((row) => row.missingCount > 0)
    : [];

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    // Debug: confirm that Save toggles validationActive and missing labels are computed as expected.
    // eslint-disable-next-line no-console
    console.warn('[experience-validation] list', {
      validationActive,
      experiencesCount: (experiences || []).length,
      errorEntryMeta,
      errorsWorkExperiences: errors?.workExperiences,
    });
  }, [validationActive, experiences, errorEntryMeta, errors]);

  const hasAnyRowErrors = validationActive && errorEntryMeta.length > 0;
  const missingFieldTotal = errorEntryMeta.reduce(
    (sum, row) => sum + row.missingCount,
    0
  );
  const errorRowCount = errorEntryMeta.length;

  const jumpToEntry = (idx) => {
    setExpandedIndex(idx);
    setTimeout(() => {
      entryCardRefs.current[idx]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 120);
  };

  useEffect(() => {
    // Important: don't keep forcing the accordion open on every render.
    // Only auto-expand + scroll once per validation attempt (keyed by Save-shake).
    if (!validationActive || !hasAnyRowErrors) return;
    if (!validationKey) return;
    if (validationKey === lastAutoFocusKeyRef.current) return;
    lastAutoFocusKeyRef.current = validationKey;
    const firstIdx = errorEntryMeta[0]?.idx ?? -1;
    if (firstIdx < 0) return;
    setExpandedIndex(firstIdx);
    const t = setTimeout(() => {
      entryCardRefs.current[firstIdx]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 200);
    return () => clearTimeout(t);
  }, [validationActive, hasAnyRowErrors, errorEntryMeta, validationKey]);

  return (
    <div className="space-y-6">
      {validationActive && hasAnyRowErrors && (
        <div className="edu-validation-banner edu-validation-banner--show">
          <div className="edu-validation-banner-inner">
            <div className="edu-validation-banner-icon">
              <AlertTriangle strokeWidth={2.5} />
            </div>
            <div className="edu-validation-banner-body">
              <div className="edu-validation-banner-title">
                {`${errorRowCount} ${
                  errorRowCount === 1 ? 'entry needs' : 'entries need'
                } attention`}
              </div>
              <div className="edu-validation-banner-text">
                {`${missingFieldTotal} required ${
                  missingFieldTotal === 1 ? 'field is' : 'fields are'
                } missing across your work experience entries.`}
              </div>
            </div>
          </div>

          {errorEntryMeta.length > 0 && (
            <div className="edu-validation-banner-links">
              {errorEntryMeta.map(({ idx, missingCount }) => {
                const label = experiences?.[idx]?.role?.trim?.()
                  ? experiences[idx].role
                  : `Entry ${idx + 1}`;
                const fieldWord = missingCount === 1 ? 'field' : 'fields';
                return (
                  <button
                    key={`exp-jump-${idx}`}
                    type="button"
                    className="edu-validation-banner-link"
                    onClick={() => jumpToEntry(idx)}
                  >
                    <span className="edu-link-dot" />
                    {label} — {missingCount} {fieldWord}
                    <ChevronRight strokeWidth={2.5} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {experiences.map((exp, index) => {
        const missingLabels = validationActive
          ? getMissingLabelsForExperience(
              exp || {},
              errors?.workExperiences?.[index] || null
            )
          : [];
        if (process.env.NODE_ENV !== 'production' && validationActive) {
          // eslint-disable-next-line no-console
          console.warn('[experience-validation] row', {
            index,
            missingLabels,
            role: exp?.role,
            company: exp?.company,
            period: exp?.employment_period,
            errorsRow: errors?.workExperiences?.[index],
          });
        }
        return (
          <div
            key={`work-exp-${index}`}
            ref={(el) => {
              entryCardRefs.current[index] = el;
            }}
          >
            <WorkExperienceFields
              experience={exp}
              index={index}
              control={control}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              errors={errors}
              liveDutyApi
              isExpanded={expandedIndex === index}
              onToggle={(open) => handleToggle(index, open)}
              onRemove={
                experiences.length > 1
                  ? () => {
                      const updated = experiences.filter((_, i) => i !== index);
                      setValue(
                        'workExperiences',
                        updated.length === 0
                          ? [getEmptyWorkExperience()]
                          : updated
                      );
                      // Keep first remaining entry open
                      setExpandedIndex(0);
                    }
                  : null
              }
              validationActive={validationActive}
              missingLabels={missingLabels}
              validationKey={validationKey}
            />
          </div>
        );
      })}
      <AddExperienceButton
        onClick={() => {
          const newIndex = experiences.length;
          setValue('workExperiences', [
            ...experiences,
            getEmptyWorkExperience(),
          ]);
          // Open the newly added entry
          setExpandedIndex(newIndex);
        }}
      />
    </div>
  );
};

// Email Change Section – inline OTP flow for changing email in the Contact edit sheet
const EmailChangeSection = ({
  currentEmail,
  onEmailVerified,
  initialVerified,
}) => {
  const [email, setEmail] = useState(currentEmail || '');
  // true when user has typed a value different from the original backend email
  const [isEdited, setIsEdited] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedThisSession, setVerifiedThisSession] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef(null);

  const startResendTimer = (seconds = 0) => {
    const safeSeconds = Number(seconds) || 0;
    setCanResend(safeSeconds <= 0);
    setResendTimer(Math.max(safeSeconds, 0));
    if (timerRef.current) clearInterval(timerRef.current);
    if (safeSeconds > 0) {
      timerRef.current = setInterval(() => {
        setResendTimer((prev) => {
          const next = prev - 1;
          if (next <= 0) {
            clearInterval(timerRef.current);
            // Small UI delay to avoid abrupt button state changes
            setTimeout(() => setCanResend(true), 300);
            return 0;
          }
          return next;
        });
      }, 1000);
    }
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
    },
    []
  );

  const validateEmail = (val) =>
    /^[A-Z0-9._%+\-]+@[A-Z0-9.\-]+\.[A-Z]{2,}$/i.test(val);

  const handleSendOTP = async () => {
    if (!email || !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    const isSameEmail =
      email.trim().toLowerCase() === (currentEmail || '').trim().toLowerCase();

    // Only block if it's the SAME email AND it was ALREADY verified initially
    if (isSameEmail && initialVerified) {
      setEmailError('New email must be different from your current email');
      return;
    }
    setEmailError('');
    setIsSendingOTP(true);
    try {
      const res = await ResumeService.requestEmailChangeOTP(email.trim());
      const status = res?.status;
      const cooldownSeconds = Number(res?.cooldown_seconds || 0);
      setOtpStep(true);
      startResendTimer(cooldownSeconds);
      if (status === 'otp_already_active') {
        // keep in OTP step and respect server cooldown
      }
    } catch (error) {
      const msg = error?.message || 'Failed to send OTP. Please try again.';
      setEmailError(msg);
    } finally {
      setIsSendingOTP(false);
    }
  };

  const handleVerifyOTP = async (otpVal) => {
    const otpToVerify = otpVal !== undefined ? otpVal : otp;
    if (!otpToVerify || otpToVerify.length !== 4) {
      setOtpError('Please enter the 4-digit OTP');
      return;
    }
    setOtpError('');
    setIsVerifying(true);
    try {
      await ResumeService.verifyEmailChange(email.trim(), otpToVerify);
      setVerifiedThisSession(true);
      setOtpStep(false);
      if (timerRef.current) clearInterval(timerRef.current);
      setResendTimer(0);
      setCanResend(false);
      // Notify parent (refetch) but do NOT close the form
      onEmailVerified?.();
    } catch (error) {
      const msg = error?.message || 'Invalid OTP. Please try again.';
      setOtpError(msg);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;
    setIsSendingOTP(true);
    try {
      const res = await ResumeService.resendEmailChangeOTP(email.trim());
      const cooldownSeconds = Number(res?.cooldown_seconds || 0);
      startResendTimer(cooldownSeconds);
      setOtp('');
      setOtpError('');
    } catch (error) {
      const msg = error?.message || 'Failed to resend OTP.';
      setOtpError(msg);
    } finally {
      setIsSendingOTP(false);
    }
  };

  const handleCancelOTP = () => {
    setOtpStep(false);
    setOtp('');
    setOtpError('');
    if (timerRef.current) clearInterval(timerRef.current);
    setResendTimer(0);
    setCanResend(false);
  };

  // 1. Not edited yet (original email from backend) AND initially verified → show "Verified" badge
  // 2. User typed a new email, OTP not yet sent → show "Verify" button (also if current email is NOT initially verified)
  // 3. OTP sent, waiting for code → show "Sent" (disabled) button
  // 4. Verified this session → show "Updated" badge
  const showVerifiedBadge =
    (!isEdited && !!currentEmail && initialVerified) || verifiedThisSession;
  const showVerifyBtn = !showVerifiedBadge && email.trim() !== '' && !otpStep;
  const showSentBtn = !showVerifiedBadge && email.trim() !== '' && otpStep;

  return (
    <div className="fg email-field-wrap">
      <label className="fl">
        Email Address
        {showVerifiedBadge ? (
          <span className="email-verified-badge">
            <CheckCircle2 style={{ width: 14, height: 14 }} />
            {verifiedThisSession ? 'Updated' : 'Verified'}
          </span>
        ) : !isEdited &&
          !!currentEmail &&
          !initialVerified &&
          !verifiedThisSession ? (
          <span className="email-verified-badge unverified">
            <XCircle
              style={{ width: 13, height: 13 }}
              className="text-yellow-600"
            />
            Unverified
          </span>
        ) : null}
      </label>

      {/* ── Row: email input + action slot ── */}
      <div className="email-input-row">
        <input
          className={`fi${emailError && !otpStep ? ' error' : ''}`}
          type="email"
          value={email}
          onChange={(e) => {
            const val = e.target.value;
            setEmail(val);
            if (emailError) setEmailError('');
            // Mark as edited only when value differs from original backend email
            const isDifferent =
              val.trim().toLowerCase() !==
              (currentEmail || '').trim().toLowerCase();
            setIsEdited(isDifferent);
            // If user clears back to original, reset OTP flow
            if (!isDifferent) {
              setOtpStep(false);
              setOtp('');
              setOtpError('');
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && showVerifyBtn) handleSendOTP();
          }}
          placeholder="you@example.com"
          disabled={otpStep || isSendingOTP}
          style={{
            background: otpStep ? 'var(--surface-muted)' : undefined,
          }}
        />

        {/* Verify button – shown only when user types a new different email */}
        {showVerifyBtn && (
          <button
            type="button"
            className="email-verify-btn"
            onClick={handleSendOTP}
            disabled={isSendingOTP}
          >
            {isSendingOTP ? 'Sending…' : 'Verify'}
          </button>
        )}

        {/* "Sent" state – email locked while user enters OTP */}
        {showSentBtn && (
          <button
            type="button"
            className="email-verify-btn"
            disabled
            style={{ opacity: 0.55 }}
          >
            Sent
          </button>
        )}
      </div>

      {/* Email validation error */}
      {emailError && !otpStep && (
        <div className="fhint" style={{ color: 'var(--destructive)' }}>
          {emailError}
        </div>
      )}

      {/* ── OTP verification panel ── */}
      {otpStep && !verifiedThisSession && (
        <div className="otp-section">
          <div className="otp-section-title">
            {isVerifying ? 'Verifying…' : 'Enter verification code'}
          </div>
          <div className="otp-section-sub">
            We&apos;ve sent a 4-digit code to <strong>{email}</strong>
          </div>

          {/* 4 OTP digit boxes */}
          <div className="otp-digits">
            {[0, 1, 2, 3].map((idx) => (
              <input
                key={idx}
                id={`ec-otp-${idx}`}
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={1}
                value={otp[idx] || ''}
                disabled={isVerifying}
                className={`otp-digit${otpError ? ' error' : otp[idx] ? ' filled' : ''}`}
                autoComplete={idx === 0 ? 'one-time-code' : 'off'}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  // Check if mobile keyboard autofilled the entire code into this input
                  if (val.length > 1) {
                    const pasted = val.slice(0, 4);
                    setOtp(pasted);
                    if (otpError) setOtpError('');
                    const finalIdx = pasted.length === 4 ? 3 : pasted.length;
                    document.getElementById(`ec-otp-${finalIdx}`)?.focus();
                    if (pasted.length === 4) handleVerifyOTP(pasted);
                    return;
                  }

                  const digit = val.slice(-1);
                  const arr = `${otp}    `.slice(0, 4).split('');
                  arr[idx] = digit;
                  const newOtp = arr.join('').trimEnd();
                  setOtp(newOtp);
                  if (otpError) setOtpError('');
                  if (digit && idx < 3) {
                    document.getElementById(`ec-otp-${idx + 1}`)?.focus();
                  }
                  if (digit && idx === 3) {
                    const fullOtp = arr.join('').replace(/\s/g, '');
                    if (fullOtp.length === 4) handleVerifyOTP(fullOtp);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace') {
                    e.preventDefault();
                    const arr = `${otp}    `.slice(0, 4).split('');
                    if (arr[idx] && arr[idx] !== ' ') {
                      arr[idx] = ' ';
                      setOtp(arr.join('').trimEnd());
                    } else if (idx > 0) {
                      arr[idx - 1] = ' ';
                      setOtp(arr.join('').trimEnd());
                      document.getElementById(`ec-otp-${idx - 1}`)?.focus();
                    }
                  } else if (e.key === 'ArrowLeft' && idx > 0) {
                    document.getElementById(`ec-otp-${idx - 1}`)?.focus();
                  } else if (e.key === 'ArrowRight' && idx < 3) {
                    document.getElementById(`ec-otp-${idx + 1}`)?.focus();
                  }
                }}
                onPaste={(e) => {
                  if (idx !== 0) return;
                  e.preventDefault();
                  const pasted = e.clipboardData
                    .getData('text')
                    .replace(/\D/g, '')
                    .slice(0, 4);
                  setOtp(pasted);
                  if (otpError) setOtpError('');
                  document
                    .getElementById(`ec-otp-${Math.min(pasted.length, 3)}`)
                    ?.focus();
                  if (pasted.length === 4) handleVerifyOTP(pasted);
                }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={idx === 0}
                aria-label={`OTP digit ${idx + 1}`}
              />
            ))}
          </div>

          {/* Error message */}
          {otpError && <div className="otp-error-msg">{otpError}</div>}

          {/* Footer: resend + change email */}
          <div className="otp-footer">
            <button
              type="button"
              className="otp-resend"
              onClick={handleResendOTP}
              disabled={!canResend || isSendingOTP}
            >
              {isSendingOTP
                ? 'Sending…'
                : canResend
                  ? 'Resend code'
                  : 'Resend code'}
            </button>
            {!canResend && !isSendingOTP && (
              <span className="otp-timer">in {resendTimer}s</span>
            )}
          </div>

          {/* Change email link */}
          <div
            style={{
              marginTop: 8,
              fontSize: '0.72rem',
              color: 'var(--text-faint)',
            }}
          >
            Wrong email?{' '}
            <button
              type="button"
              className="otp-resend"
              onClick={handleCancelOTP}
              style={{ fontSize: '0.72rem' }}
            >
              Change email
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Accordion Card Component (Mobile) / Static Card (Desktop)
const AccordionCard = ({
  title,
  subtitle,
  icon,
  isComplete,
  isOpen,
  onToggle,
  children,
  onEdit,
  showEdit = true,
  hideEditWhenIncomplete = false,
  editButtonText = 'Edit',
  editButtonClassName = '',
  customBadgeText = null,
  customBadgeCount = null,
  className = '',
}) => {
  const titleRef = useRef(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const bodyId = `accordion-body-${title
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')}`;

  // Check if title is truncated
  useEffect(() => {
    const checkTruncation = () => {
      if (titleRef.current) {
        const isTruncated =
          titleRef.current.scrollWidth > titleRef.current.clientWidth;
        setIsTitleTruncated(isTruncated);
      }
    };

    checkTruncation();
    // Recheck on window resize
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [title]);

  return (
    <div
      className={`accordion-card ${isOpen ? 'open' : ''} ${!isComplete ? 'incomplete' : ''} ${className}`}
    >
      {/* Header - Clickable on mobile, static on desktop */}
      <div
        className="accordion-header"
        onClick={(e) => {
          onToggle(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(e);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-controls={bodyId}
      >
        <div className="accordion-icon">{icon}</div>
        <div className="accordion-info">
          <div className="accordion-title-row">
            {isTitleTruncated ? (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <div ref={titleRef} className="accordion-title">
                      {title}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    align="start"
                    className="text-xs border border-[var(--border)] max-w-300px word-break-break-word text-0.7rem"
                  >
                    <p>{title}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <div ref={titleRef} className="accordion-title">
                {title}
              </div>
            )}
            {/* Desktop: custom badge inline with title (only for completed sections with counts) */}
            {customBadgeText && customBadgeCount !== null ? (
              <span className="badge badge-complete badge-count badge-desktop-inline">
                <span className="badge-dot" />
                <span className="badge-label">
                  {customBadgeCount} {customBadgeText}
                </span>
              </span>
            ) : null}
          </div>
          <div className="accordion-subtitle">{subtitle}</div>
        </div>
        {/* Desktop: Incomplete badge at right end of header */}
        {!isComplete && (hideEditWhenIncomplete || !showEdit) && (
          <span className="badge badge-incomplete badge-desktop-right-end">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              style={{ width: 11, height: 11, flexShrink: 0 }}
            >
              <circle cx="12" cy="12" r="9" />
            </svg>
            <span className="badge-label">Incomplete</span>
          </span>
        )}
        {/* Mobile: badge right-aligned in header */}
        {customBadgeText && customBadgeCount !== null ? (
          <span className="badge badge-complete badge-count badge-mobile-right">
            <span className="badge-dot" />
            {customBadgeCount} {customBadgeText}
          </span>
        ) : !isComplete ? (
          <span className="badge badge-incomplete badge-mobile-right">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              style={{ width: 11, height: 11, flexShrink: 0 }}
            >
              <circle cx="12" cy="12" r="9" />
            </svg>
            Incomplete
          </span>
        ) : null}
        {/* Mobile: Edit button that appears when card opens (badge fades out) */}
        {showEdit && !isComplete && (
          <button
            type="button"
            className="badge-hover-edit"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <Edit style={{ width: '10px', height: '10px' }} />
            Edit
          </button>
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
          }}
        >
          {/* Desktop: incomplete + editable → morphing pill (Incomplete ↔ Edit) */}
          {!isComplete && showEdit && !hideEditWhenIncomplete ? (
            <button
              type="button"
              className="accordion-incomplete-pill"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              <span className="pill-state-incomplete">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  style={{ width: 11, height: 11, flexShrink: 0 }}
                >
                  <circle cx="12" cy="12" r="9" />
                </svg>
                Incomplete
              </span>
              <span className="pill-state-edit">
                <Edit style={{ width: 11, height: 11 }} />
                Edit
              </span>
            </button>
          ) : (
            /* Complete section → regular edit button */
            showEdit &&
            (!hideEditWhenIncomplete || isComplete) && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className={`d-edit-btn ${hideEditWhenIncomplete && isComplete ? 'd-edit-btn-pill-style' : ''} ${editButtonClassName}`}
                title={editButtonText}
              >
                <Edit style={{ width: '12px', height: '12px' }} />
                <span>{editButtonText}</span>
              </button>
            )
          )}
          {/* Chevron - Hidden on desktop */}
          <div className="accordion-chevron">
            <ChevronDown style={{ width: '16px', height: '16px' }} />
          </div>
        </div>
      </div>
      {/* Content - Collapsible on mobile, always visible on desktop */}
      <div id={bodyId} className="accordion-body">
        <div className="accordion-body-inner">
          {children}
          {/* Edit Button - Mobile only, shown at bottom of content */}
          {showEdit && (!hideEditWhenIncomplete || isComplete) && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="edit-btn flex items-center gap-2"
            >
              <Edit style={{ width: '14px', height: '14px' }} />
              <span>Edit {title}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Field Display Component
const FieldDisplay = ({ label, value, icon, badge, emptyText = '--' }) => {
  if (!value || value === 'N/A' || value === '-') {
    return (
      <div className="field-display">
        <div className="field-label">
          {label}
          {badge && badge}
        </div>
        <div className="field-value">
          <span style={{ color: 'var(--text-faint)' }}>{emptyText}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="field-display">
      <div className="field-label">
        {label}
        {badge && badge}
      </div>
      <div className="field-value">
        {icon && icon}
        <span>{value}</span>
      </div>
    </div>
  );
};

// Completed Stack Component (Mobile) - Similar to ProfileDashboard
const CompletedStack = ({
  completedCards,
  openCardKey,
  onToggle,
  renderCardContent,
  onEdit,
  stackFooter,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (completedCards.length < 1) return null;

  const names = completedCards
    .map((c) => {
      const title = c.title || '';
      // Truncate long section names to keep preview compact
      if (title.length > 15) {
        return `${title.substring(0, 12)}...`;
      }
      return title;
    })
    .filter(Boolean);
  const previewNames =
    names.slice(0, 2).join(', ') +
    (names.length > 2 ? ` +${names.length - 2} more` : '');

  return (
    <div className={`completed-stack${expanded ? ' expanded' : ''}`}>
      <div
        className="stack-trigger"
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        <div className="stack-header">
          <div className="stack-icon">
            <CheckCircle2 className="w-4 h-4" />
          </div>
          <div className="stack-info">
            <div className="stack-title">Completed Sections</div>
            <div className="stack-sub">{previewNames}</div>
          </div>
          <span className="stack-count-badge">
            <span className="badge-dot" />
            {completedCards.length} done
          </span>
          <div className="stack-chevron">
            <ChevronDown style={{ width: '16px', height: '16px' }} />
          </div>
        </div>
      </div>
      <div className="stack-cards">
        {completedCards.map((section) => {
          const isOpen = openCardKey === section.key;
          return (
            <div
              key={section.key}
              className={[
                'accordion-card',
                isOpen && 'open',
                section.key === 'experience' && 'accordion-card-experience',
                section.key === 'education' && 'accordion-card-education',
                (section.key === 'preferences' ||
                  section.key === 'compensation' ||
                  section.key === 'jobPreferences') &&
                  'accordion-card-preferences',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <div
                className="accordion-header"
                onClick={() => onToggle(section.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onToggle(section.key);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                aria-controls={`accordion-body-stack-${section.key}`}
              >
                <div className="accordion-icon">{section.icon}</div>
                <div className="accordion-info">
                  <div className="accordion-title-row">
                    <div className="accordion-title">{section.title}</div>
                  </div>
                  <div className="accordion-subtitle">{section.subtitle}</div>
                </div>
                <span className="badge badge-complete stack-card-badge">
                  <span className="badge-dot" />
                  Done
                </span>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexShrink: 0,
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(section.key);
                    }}
                    className="d-edit-btn"
                    title="Edit"
                  >
                    <Edit style={{ width: '12px', height: '12px' }} />
                    <span>Edit</span>
                  </button>
                  <div className="accordion-chevron">
                    <ChevronDown style={{ width: '16px', height: '16px' }} />
                  </div>
                </div>
              </div>
              <div
                id={`accordion-body-stack-${section.key}`}
                className="accordion-body"
              >
                <div className="accordion-body-inner">
                  {renderCardContent(section)}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(section.key);
                    }}
                    className="edit-btn flex items-center gap-2"
                  >
                    <Edit style={{ width: '14px', height: '14px' }} />
                    <span>Edit {section.title}</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {stackFooter}
      </div>
    </div>
  );
};

const SectionGroup = ({
  id,
  title,
  iconClassName,
  icon,
  subtitle,
  count,
  expanded,
  hasOpenCard = false,
  onToggle,
  children,
}) => {
  const cardsRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState('0px');

  useEffect(() => {
    const el = cardsRef.current;
    if (!el) return;

    if (!expanded) {
      setMaxHeight('0px');
      return;
    }

    const measure = () => {
      // Use scrollHeight so expanding inner accordions grows container.
      setMaxHeight(`${el.scrollHeight || 0}px`);
    };

    measure();

    // Keep height in sync while inner accordions expand/collapse.
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);

    return () => ro.disconnect();
  }, [expanded, children]);

  return (
    <div className={`section-group${expanded ? ' expanded' : ''}`} id={id}>
      <div
        className="group-trigger"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="group-header">
          <div className={`group-icon ${iconClassName}`}>{icon}</div>
          <div className="group-info">
            <div className="group-title">{title}</div>
            <div className="group-sub">{subtitle}</div>
          </div>
          <span className="group-count">
            <span className="badge-dot" />
            {count}
          </span>
          <div className="group-chevron">
            <ChevronDown style={{ width: '16px', height: '16px' }} />
          </div>
        </div>
      </div>
      <div
        className="group-cards"
        ref={cardsRef}
        style={{ maxHeight, overflow: hasOpenCard ? 'clip' : undefined }}
      >
        {children}
      </div>
    </div>
  );
};

// Bottom Sheet/Modal Component
const EditSheet = ({
  isOpen,
  onClose,
  title,
  children,
  onSave,
  isLoading,
  section,
  saveShakeKey = 0,
  educationEntryCount = 0,
}) => {
  const scrollContainerRef = useRef(null);
  const lastShakeKeyRef = useRef(0);
  const [primarySaveShake, setPrimarySaveShake] = useState(false);

  useEffect(() => {
    if (saveShakeKey > 0 && saveShakeKey !== lastShakeKeyRef.current) {
      lastShakeKeyRef.current = saveShakeKey;
      setPrimarySaveShake(true);
      const t = setTimeout(() => setPrimarySaveShake(false), 500);
      return () => clearTimeout(t);
    }
  }, [saveShakeKey]);

  if (!isOpen) return null;

  // Determine width based on section type
  const getWidthClass = () => {
    const largeSections = ['contact', 'education', 'experience'];
    const mediumSections = [
      'compensation',
      'jobPreferences',
      'languages',
      'summary',
    ];

    if (largeSections.includes(section)) {
      return 'md:min-w-[26rem] md:max-w-lg';
    }
    if (mediumSections.includes(section)) {
      return 'md:min-w-[22rem] md:max-w-lg';
    }
    return 'md:min-w-[20rem] md:max-w-lg';
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100000] transition-opacity"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onClose();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />
      <div
        data-section={section}
        data-section-edit-sheet=""
        className={`fixed inset-x-4 bottom-0 md:inset-x-0 md:bottom-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-[100001] bg-[var(--surface)] border border-[var(--border)] rounded-t-2xl md:rounded-2xl shadow-2xl min-h-[80vh] md:min-h-[400px] max-h-[90vh] ${getWidthClass()} md:w-auto flex flex-col transition-transform ${
          isOpen ? 'translate-y-0' : 'translate-y-full md:scale-95 md:opacity-0'
        }`}
      >
        <div className="sheet-handle" />
        <div className="flex items-start justify-between p-4 border-b border-[var(--border-light)] flex-shrink-0">
          <div className="flex items-center gap-2">
            <h3 className="sheet-title">{title}</h3>
            {section === 'education' && (
              <span className="edu-form-entry-count">
                {educationEntryCount}{' '}
                {educationEntryCount === 1 ? 'entry' : 'entries'}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-[var(--surface-muted)] text-[var(--text-muted)] transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div
          ref={scrollContainerRef}
          className="edit-form-body overflow-y-auto flex-1 p-4 bg-[var(--bg)]"
        >
          {children}
        </div>
        <div className="flex gap-3 p-4 border-t border-[var(--border-light)] flex-shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            className={`flex-1 bg-ah-brand-primary hover:bg-ah-brand-primary-hover text-white ${
              primarySaveShake ? 'edu-save-shake' : ''
            }`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    </>
  );
};

/**
 * Topbar "Export Profile" button. Fetches the server-generated resume PDF and
 * opens it in a new tab. The blank tab is opened synchronously within the click
 * gesture (so it isn't popup-blocked), then pointed at the PDF blob once it
 * arrives; if the tab was blocked, it falls back to a direct download.
 */
function ExportProfileButton({ fileName }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const exportPdfMutation = useResumePdf();

  const handleExport = async () => {
    if (loading) return;
    // Open the tab up-front within the user gesture to avoid popup blocking.
    const tab = window.open('', '_blank');
    setLoading(true);
    try {
      const blob = await exportPdfMutation.mutateAsync();
      const url = URL.createObjectURL(blob);
      if (tab) {
        tab.location = url;
      } else {
        // Popup blocked — fall back to a direct download.
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Release the blob URL once the new tab has had time to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      if (tab) tab.close();
      toast({
        message: 'Could not export the resume. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      className="inline-flex h-[25px] w-[30px] shrink-0 items-center justify-center gap-1.5 rounded-[var(--radius-xs)] border border-[var(--border)] bg-[var(--surface)] text-[0.78rem] font-semibold tracking-[-0.005em] text-[var(--text-muted)] transition-colors hover:border-[var(--primary)] hover:bg-[var(--primary-ghost)] hover:text-[var(--primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:cursor-not-allowed disabled:opacity-60 sm:h-8 sm:w-auto sm:px-3.5"
      disabled={loading}
      onClick={handleExport}
      title="Export Profile"
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <Download className="h-3.5 w-3.5" aria-hidden />
      )}
      <span className="hidden sm:inline">Export Profile</span>
    </button>
  );
}

export default function ResumeDashboard() {
  const { toast } = useToast();

  const candidateId = useMemo(() => {
    const userData = getUser();
    return userData?.cid || null;
  }, []);

  const queryClient = useQueryClient();
  // Reference data first: shared cache with useProtectedResumeProfile + explicit prefetch on mount.
  const { data: referenceData } = useReferenceData();
  const {
    data: resumeData,
    isLoading: isLoadingData,
    refetch,
  } = useProtectedResumeProfile(candidateId);

  // Write mutations — each invalidates the protected profile query on success
  // (replaces the manual refetch() the save flows used to call).
  const updateProfileSectionMutation = useUpdateProfileSection();
  const updateResumeSectionMutation = useUpdateResumeSection(candidateId);
  const saveWorkExperiencesMutation = useSaveWorkExperiences();

  useEffect(() => {
    void queryClient.prefetchQuery(REFERENCE_DATA_QUERY_OPTIONS);
  }, [queryClient]);

  const resumeProgressMeta = useMemo(
    () =>
      buildResumeUploadProgressMeta(referenceData?.resumeUploadProcessLabels),
    [referenceData?.resumeUploadProcessLabels]
  );
  const maxListStepIndexRef = useRef(resumeProgressMeta.maxListStepIndex);
  useEffect(() => {
    maxListStepIndexRef.current = resumeProgressMeta.maxListStepIndex;
  }, [resumeProgressMeta.maxListStepIndex]);
  const emailVerified = resumeData?.emailVerified === true;

  const effectiveCandidateId = useMemo(() => {
    if (candidateId != null && String(candidateId).trim() !== '')
      return candidateId;
    const fromProfile = resumeData?.candidateId;
    if (fromProfile != null && String(fromProfile).trim() !== '')
      return fromProfile;
    return null;
  }, [candidateId, resumeData?.candidateId]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (!resumeData) return;
    // eslint-disable-next-line no-console
    console.debug('[resume] certifications completion inputs', {
      noCertifications: resumeData.noCertifications,
      certifications_len: Array.isArray(resumeData.certifications)
        ? resumeData.certifications.length
        : null,
    });
  }, [resumeData]);

  const [openSections, setOpenSections] = useState({});
  const [openMobileCard, setOpenMobileCard] = useState(null);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [currentEditSection, setCurrentEditSection] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [educationValidationActive, setEducationValidationActive] =
    useState(false);
  const [educationSaveShakeKey, setEducationSaveShakeKey] = useState(0);
  const [experienceValidationActive, setExperienceValidationActive] =
    useState(false);
  const [experienceSaveShakeKey, setExperienceSaveShakeKey] = useState(0);
  const [resumeApi, setResumeApi] = useState(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isTypingSummary, setIsTypingSummary] = useState(false);
  const [isReadingProfileSummary, setIsReadingProfileSummary] = useState(false);
  const typingIntervalRef = useRef(null);
  const [summaryJustGenerated, setSummaryJustGenerated] = useState(false);
  const [hasTriggeredAutoGenerate, setHasTriggeredAutoGenerate] =
    useState(false);
  const [showAutoGenerateButton, setShowAutoGenerateButton] = useState(false);
  const [writeOwnExpanded, setWriteOwnExpanded] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [processingPercent, setProcessingPercent] = useState(2);
  const [showCompletionCelebration, setShowCompletionCelebration] =
    useState(false);
  const celebrationTriggeredRef = useRef(false);
  const [certificationsNA, setCertificationsNA] = useState(false);
  const [experienceNA, setExperienceNA] = useState(false);
  const [initialEduIndex, setInitialEduIndex] = useState(null);
  const [initialExpIndex, setInitialExpIndex] = useState(null);

  // Job Preferences extra fields (local state — wired to API when config arrives)
  const [jobPrefsWorkMode, setJobPrefsWorkMode] = useState([]);
  const [jobPrefsCompanySizes, setJobPrefsCompanySizes] = useState([]);
  const [jobPrefsRelocation, setJobPrefsRelocation] = useState(null);
  const [jobPrefsJobTypes, setJobPrefsJobTypes] = useState([]);
  const [jobPrefsIndustries, setJobPrefsIndustries] = useState([]);
  const [jobPrefsIndustrySearch, setJobPrefsIndustrySearch] = useState('');

  // Languages (local state — wired to API when config arrives)
  const [langRows, setLangRows] = useState([]);
  const [langDropdownOpen, setLangDropdownOpen] = useState(null); // index of open dropdown
  const [langDropdownSearch, setLangDropdownSearch] = useState('');
  const langComboRefs = useRef([]);
  const [langDropdownRect, setLangDropdownRect] = useState(null);

  // Keep the portaled dropdown anchored when the modal scrolls or the window resizes
  useEffect(() => {
    if (langDropdownOpen === null) return;
    const update = () => {
      const el = langComboRefs.current[langDropdownOpen];
      if (el) setLangDropdownRect(el.getBoundingClientRect());
      else setLangDropdownOpen(null);
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [langDropdownOpen]);

  // Unconfirmed-skills boost card: { show, count } from /candidates/skills/unconfirmed.
  // Unconfirmed-skills boost card, driven by TanStack Query. Enabled once the
  // profile has loaded (mirrors the previous one-shot fetch); it re-syncs when
  // the skills page invalidates ['skills','unconfirmed'] after a confirm/edit.
  const { data: unconfirmedData } = useUnconfirmedSkills({
    enabled: !!candidateId && !isLoadingData && !!resumeData,
  });
  const unconfirmedSkills = {
    show: unconfirmedData?.hasUnconfirmed ?? false,
    count: unconfirmedData?.count ?? 0,
  };

  /** null = closed; upload | processing | success | failed — same modal as reference HTML */
  const [resumeOverlaySurface, setResumeOverlaySurface] = useState(null);
  const [userSkippedResumeUpload, setUserSkippedResumeUpload] = useState(false);
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [uploadProcessing, setUploadProcessing] = useState(false);
  const [isUploadStatusPolling, setIsUploadStatusPolling] = useState(false);
  const [resumeProcessed, setResumeProcessed] = useState(false);
  /** Step index shown as failed in the popup (matches reference HTML). */
  const [processingFailedAtStep, setProcessingFailedAtStep] = useState(0);
  /** Optional label from upload-processing API (step-2 interpolation). */
  const [processingApiLabel, setProcessingApiLabel] = useState(null);
  const resumeUploadHook = useResumeUpload();
  const fileInputRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const pollCountRef = useRef(0);
  const resumeFlowBootstrapDoneRef = useRef(false);
  const processingStepRef = useRef(0);
  const uploadProcessingFirstPollDelayMsRef = useRef(
    UPLOAD_PROCESSING_FIRST_POLL_DELAY_MS
  );

  useEffect(() => {
    processingStepRef.current = currentStep;
  }, [currentStep]);

  const {
    control,
    watch,
    reset,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: resumeData || defaultValues,
    mode: 'onChange',
  });

  const educationEntryCount = (watch('educations') || []).length;

  // Initialize resumeApi
  useEffect(() => {
    if (candidateId) {
      const api = createResumeApi({
        isProtected: true,
        candidateId,
      });
      setResumeApi(api);
    }
  }, [candidateId]);

  // Sync form with resumeData
  useEffect(() => {
    if (resumeData) {
      // Normalize CTC values to ensure they're rounded to 2 decimal places when converted to LPA
      const normalizedData = {
        ...resumeData,
        workExperiences: mapWorkExperiencesWithDuties(
          resumeData.workExperiences
        ),
        // Normalize currentCTC: round the LPA value to 2 decimals, then convert back
        currentCTC: resumeData.currentCTC
          ? Math.min(
              Math.round(convertToLPA(resumeData.currentCTC) * 100000),
              CTC_MAX_AMOUNT
            )
          : resumeData.currentCTC,
        // Normalize expectedCTC: round the LPA value to 2 decimals, then convert back
        expectedCTC: resumeData.expectedCTC
          ? Math.min(
              Math.round(convertToLPA(resumeData.expectedCTC) * 100000),
              CTC_MAX_AMOUNT
            )
          : resumeData.expectedCTC,
        // Convert DOB from API format (DD/MM/YYYY) to date input format (YYYY-MM-DD)
        dob: resumeData.dob
          ? convertToDateInputFormat(resumeData.dob)
          : resumeData.dob,
      };
      reset(normalizedData);
    }
  }, [resumeData, reset]);

  // Sync NA states from API response and processing status
  useEffect(() => {
    if (resumeData) {
      setExperienceNA(Boolean(resumeData.noWorkExp));
      setCertificationsNA(resumeData.noCertifications === true);

      // Only check resume_processed from otherDetails (upload_processing comes from separate API)
      const resumeProcessedValue = Boolean(
        resumeData.otherDetails?.resume_processed
      );

      setResumeProcessed(resumeProcessedValue);
    }
  }, [resumeData]);

  useEffect(() => {
    resumeFlowBootstrapDoneRef.current = false;
  }, [candidateId]);

  // If profile gains resume_processed while upload prompt is open, close it.
  useEffect(() => {
    if (!resumeData) return;
    const processed = Boolean(resumeData.otherDetails?.resume_processed);
    if (processed && resumeOverlaySurface === 'upload') {
      setResumeOverlaySurface(null);
    }
  }, [
    resumeData,
    resumeData?.otherDetails?.resume_processed,
    resumeOverlaySurface,
  ]);

  // Cleanup typing interval on unmount
  useEffect(
    () => () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
    },
    []
  );

  // Detect desktop viewport (md breakpoint is 768px in Tailwind)
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    // Check on mount
    checkDesktop();

    // Check on resize
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  const { setHeaderEndContent } = useOutletContext() ?? {};

  useEffect(() => {
    if (!resumeData || !setHeaderEndContent) return undefined;
    // Resume download lives in the topbar (matches positions.html .topbar-actions
    // "Export Profile" button). Only offered once the resume has been processed.
    if (!resumeProcessed) {
      setHeaderEndContent(null);
      return undefined;
    }
    const headerDownload = (
      <ExportProfileButton
        fileName={`${resumeData.firstname}_${resumeData.lastname}_resume.pdf`}
      />
    );
    setHeaderEndContent(headerDownload);
    return () => setHeaderEndContent(null);
  }, [resumeData, setHeaderEndContent, resumeProcessed]);

  // Measure the sticky top-bar height and store as --header-h CSS variable.
  // Also detect when the open accordion header is "stuck" to add a shadow.
  // Mirrors the exact behaviour in the HTML reference file.
  useEffect(() => {
    if (isDesktop) return; // mobile only

    const setHeaderHeight = () => {
      // The sticky wrapper in MainLayout renders a <header> with class h-14.
      // Walk up from it to find the nearest sticky-positioned ancestor.
      const headerEl = document.querySelector('header');
      if (!headerEl) return;
      let el = headerEl.parentElement;
      let stickyEl = null;
      while (el && el !== document.body) {
        if (window.getComputedStyle(el).position === 'sticky') {
          stickyEl = el;
          break;
        }
        el = el.parentElement;
      }
      const height = (stickyEl || headerEl).offsetHeight;
      document.documentElement.style.setProperty('--header-h', `${height}px`);
    };

    const handleScroll = () => {
      const openCard = document.querySelector('.accordion-card.open');
      if (openCard) {
        const cardHeader = openCard.querySelector('.accordion-header');
        const headerH =
          parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue(
              '--header-h'
            )
          ) || 56;
        if (cardHeader) {
          const rect = cardHeader.getBoundingClientRect();
          cardHeader.classList.toggle(
            'stuck',
            Math.abs(rect.top - headerH) < 3
          );
        }
      }
    };

    setHeaderHeight();
    window.addEventListener('resize', setHeaderHeight);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', setHeaderHeight);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isDesktop]);

  // Lock body scroll when edit modal is open
  useEffect(() => {
    if (editSheetOpen) {
      // Save the current overflow value
      const originalOverflow = document.body.style.overflow;
      // Lock body scroll
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore original overflow value when modal closes
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [editSheetOpen]);

  useEffect(() => {
    if (resumeOverlaySurface == null) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [resumeOverlaySurface]);

  const parseBool = (value) => value === true || value === 'true';

  // Fetch upload-processing status (new API: status + step/percent/label; legacy: boolean)
  const fetchUploadProcessingStatus = React.useCallback(
    async ({ showErrorToast = false } = {}) => {
      if (!candidateId) return null;

      try {
        const payload =
          await ResumeService.getUploadProcessingStatus(candidateId);
        const isProc = payload.status === 'processing';
        setUploadProcessing(isProc);
        if (isProc) {
          if (typeof payload.step === 'number') {
            setCurrentStep(
              Math.min(
                Math.max(0, payload.step),
                resumeProgressMeta.maxListStepIndex
              )
            );
          }
          if (typeof payload.percent === 'number') {
            setProcessingPercent(payload.percent);
          }
          setProcessingApiLabel(
            payload.label != null && payload.label !== '' ? payload.label : null
          );
        }
        return payload;
      } catch (error) {
        if (showErrorToast) {
          toast({
            message:
              error?.message ||
              'Unable to check upload processing status. Please try again.',
            variant: 'destructive',
          });
        }
        return null;
      }
    },
    [candidateId, toast, resumeProgressMeta.maxListStepIndex]
  );

  /**
   * On load: GET upload-processing first.
   * - processing → modal with progress + polling
   * - else → if resume not processed and user didn’t skip, modal with upload (no inline card)
   */
  useEffect(() => {
    if (!candidateId || isLoadingData || !resumeData) return;
    if (resumeFlowBootstrapDoneRef.current) return;
    resumeFlowBootstrapDoneRef.current = true;

    const run = async () => {
      const payload = await fetchUploadProcessingStatus({
        showErrorToast: false,
      });
      if (!payload) return;

      if (payload.status === 'processing') {
        // We already performed the "bootstrap" status call just now.
        // To avoid any accidental shorter delay from a previous session, explicitly
        // schedule the first poll after the standard 5s interval.
        uploadProcessingFirstPollDelayMsRef.current =
          UPLOAD_PROCESSING_FIRST_POLL_DELAY_MS;
        setResumeOverlaySurface('processing');
        setIsUploadStatusPolling(true);
        pollCountRef.current = 0;
        if (typeof payload.percent !== 'number') {
          setProcessingPercent(getProcessingStartPercent());
        }
        if (typeof payload.step !== 'number') {
          setCurrentStep(0);
        }
        return;
      }

      const processed = Boolean(resumeData.otherDetails?.resume_processed);
      if (!processed && !userSkippedResumeUpload) {
        setResumeOverlaySurface('upload');
      }
    };
    run();
  }, [
    candidateId,
    isLoadingData,
    resumeData,
    fetchUploadProcessingStatus,
    userSkippedResumeUpload,
  ]);

  // Backend says processing → keep modal on progress surface (unless success/failed).
  useEffect(() => {
    if (!candidateId || isLoadingData) return;
    if (uploadProcessing) {
      setResumeOverlaySurface((s) =>
        s === 'success' || s === 'failed' ? s : 'processing'
      );
      setIsUploadStatusPolling(true);
    }
  }, [candidateId, isLoadingData, uploadProcessing]);

  // Polling logic:
  // - after overlay starts, wait 5s (default) or 4s (dashboard upload) then call status API once
  // - then poll every 5s until backend says upload_processing is false
  // Stabilize function references used inside polling with refs so the effect
  // does not re-run unnecessarily (causing repeated 2s first-poll scheduling).
  const fetchUploadProcessingStatusRef = useRef(fetchUploadProcessingStatus);
  const refetchRef = useRef(refetch);
  const toastRef = useRef(toast);
  const clearFileRef = useRef(resumeUploadHook?.clearFile);
  useEffect(() => {
    fetchUploadProcessingStatusRef.current = fetchUploadProcessingStatus;
  }, [fetchUploadProcessingStatus]);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);
  useEffect(() => {
    clearFileRef.current = resumeUploadHook?.clearFile;
  }, [resumeUploadHook]);

  useEffect(() => {
    if (!candidateId || !isUploadStatusPolling) {
      // Clear polling if not polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      return;
    }

    // Poll upload-processing only while status === `processing`.
    // When status is `completed`, perform a single profile GET (via react-query refetch)
    // to read resume_processed — do not call getProfile between polls.
    const pollOnce = async () => {
      const payload = await fetchUploadProcessingStatusRef.current();

      const serverDrivesProgress =
        payload &&
        payload.status === 'processing' &&
        typeof payload.step === 'number' &&
        typeof payload.percent === 'number';

      if (
        !serverDrivesProgress &&
        payload?.status === 'processing' &&
        pollCountRef.current > 0
      ) {
        setProcessingPercent((prev) =>
          Math.min(prev + getProcessingPollIncrement(), 95)
        );
        setCurrentStep((prev) =>
          Math.min(prev + 1, maxListStepIndexRef.current)
        );
      }

      if (!payload) {
        pollCountRef.current += 1;
        return;
      }

      if (payload.status === 'processing') {
        pollCountRef.current += 1;
        return;
      }

      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      setIsUploadStatusPolling(false);

      const pollsBeforeThisResponse = pollCountRef.current;

      if (payload.status === 'failed') {
        setProcessingFailedAtStep(processingStepRef.current);
        setResumeOverlaySurface('failed');
        clearFileRef.current?.();
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        toastRef.current({
          message: 'Resume processing failed. Please try uploading again.',
          variant: 'destructive',
        });
        pollCountRef.current += 1;
        return;
      }

      // New API: only `completed` should trigger a profile read for resume_processed.
      // Legacy boolean API maps done → `awaiting_upload`; allow one profile refetch then.
      const shouldVerifyResumeProcessed =
        payload.status === 'completed' ||
        (payload.status === 'awaiting_upload' && pollsBeforeThisResponse > 0);

      if (!shouldVerifyResumeProcessed) {
        pollCountRef.current += 1;
        return;
      }

      try {
        const { data } = await refetchRef.current();
        const resumeProcessedValue = Boolean(
          data?.otherDetails?.resume_processed
        );
        setResumeProcessed(resumeProcessedValue);

        if (resumeProcessedValue === true) {
          if (typeof payload.percent === 'number') {
            setProcessingPercent(payload.percent);
          } else {
            setProcessingPercent(100);
          }
          if (typeof payload.step === 'number') {
            setCurrentStep(
              Math.min(Math.max(0, payload.step), maxListStepIndexRef.current)
            );
          } else {
            setCurrentStep(maxListStepIndexRef.current);
          }
          setProcessingApiLabel(null);
          setResumeOverlaySurface('success');
        } else {
          setProcessingFailedAtStep(processingStepRef.current);
          setResumeOverlaySurface('failed');
          clearFileRef.current?.();
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          toastRef.current({
            message: 'Resume processing failed. Please try uploading again.',
            variant: 'destructive',
          });
        }
      } catch (error) {
        toastRef.current({
          message:
            error?.message ||
            'Unable to refresh your profile. Please try again.',
          variant: 'destructive',
        });
      }

      pollCountRef.current += 1;
    };

    // First poll after configurable delay (ref: default 5s vs dashboard upload 4s), then poll every 5s
    // Guard: avoid scheduling if already scheduled
    if (pollingTimeoutRef.current || pollingIntervalRef.current) {
      return () => {};
    }
    const initialPollDelayMs = uploadProcessingFirstPollDelayMsRef.current;
    uploadProcessingFirstPollDelayMsRef.current =
      UPLOAD_PROCESSING_FIRST_POLL_DELAY_MS;
    pollingTimeoutRef.current = setTimeout(() => {
      pollOnce();
      pollingIntervalRef.current = setInterval(
        pollOnce,
        UPLOAD_PROCESSING_POLL_INTERVAL_MS
      );
    }, initialPollDelayMs);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
    // Effect depends only on candidate and polling flag; other function references are stabilized via refs above
  }, [candidateId, isUploadStatusPolling]);

  // NOTE: Legacy "localStorage + 60s gate" overlay logic was removed.
  // The overlay must be driven ONLY by backend `other_details.upload_processing`.

  const processingProgress =
    resumeOverlaySurface === 'success' ? 100 : processingPercent;

  const toggleSection = (section) => {
    setOpenSections((prev) => {
      const wasOpen = prev[section];
      const updated = { ...prev };

      // Get current sections dynamically
      const { completed: currentCompleted, incomplete: currentIncomplete } =
        getAllSections();
      const completedKeys = currentCompleted.map((s) => s.key);
      const incompleteKeys = currentIncomplete.map((s) => s.key);

      // Check if we're showing a stack (only when there are incomplete sections)
      const isShowingStack =
        completedKeys.length >= 1 && incompleteKeys.length > 0;

      // Close ALL open sections first — only one accordion open at a time
      Object.keys(updated).forEach((key) => {
        updated[key] = false;
      });

      // If clicking a nested section inside the "Completed Sections" stack
      if (
        section !== 'completed' &&
        completedKeys.includes(section) &&
        isShowingStack
      ) {
        // Only allow opening if parent stack is already open
        if (!prev.completed) {
          return prev;
        }
        // Keep parent stack open and toggle the nested card
        updated.completed = true;
        updated[section] = !wasOpen;
      }
      // If clicking the parent "Completed Sections" stack trigger
      else if (section === 'completed') {
        updated[section] = !wasOpen;
        // Nested cards stay closed (already cleared above)
      }
      // Any other section (incomplete or all-complete accordion)
      else {
        updated[section] = !wasOpen;
      }

      return updated;
    });
  };

  // Toggle mobile card (for CompletedStack)
  const toggleMobileCard = (key) => {
    setOpenMobileCard((prev) => (prev === key ? null : key));
  };

  // Render card content for any section (for CompletedStack)
  const renderCardContent = (section) => section.renderContent();

  const openEditSheet = (section) => {
    setCurrentEditSection(section);
    setEditSheetOpen(true);
    // Keep accordions closed by default for a clean start
    if (section === 'experience') {
      setInitialExpIndex(null);
      setExperienceValidationActive(false);
    }
    // Reset auto-generate button state when opening summary form
    if (section === 'summary') {
      setSummaryJustGenerated(false);
      setHasTriggeredAutoGenerate(false);
      setWriteOwnExpanded(false);
      // Trigger animation to show button
      setTimeout(() => {
        setShowAutoGenerateButton(true);
      }, 100); // Small delay for smooth entrance
    }
    // Initialise Job Preferences local state from resumeData
    if (section === 'jobPreferences') {
      setJobPrefsWorkMode(resumeData?.workMode || []);
      setJobPrefsCompanySizes(resumeData?.companySizes || []);
      setJobPrefsRelocation(resumeData?.openToRelocation ?? null);
      setJobPrefsJobTypes(
        Array.isArray(resumeData?.jobType) ? resumeData.jobType : []
      );
      setJobPrefsIndustries(resumeData?.preferredIndustryIds || []);
      setJobPrefsIndustrySearch('');
    }
    // Initialise Languages local state from resumeData
    if (section === 'languages') {
      const existing = Array.isArray(resumeData?.languages)
        ? resumeData.languages
        : [];
      setLangRows(
        existing.map((l) => ({
          id: l.id ?? null,
          name: l.name ?? '',
          proficiency: l.proficiency ?? 'CONVERSATIONAL',
          isPrimary: !!l.isPrimary,
        }))
      );
      setLangDropdownOpen(null);
      setLangDropdownSearch('');
    }
  };

  const openExperienceEditSheetAt = (index) => {
    setInitialExpIndex(index);
    setExperienceValidationActive(false);
    setCurrentEditSection('experience');
    setEditSheetOpen(true);
  };

  const closeEditSheet = () => {
    setEditSheetOpen(false);
    setCurrentEditSection(null);
    setEducationValidationActive(false);
    setExperienceValidationActive(false);
    // Reset summary generation state when closing
    setSummaryJustGenerated(false);
    setHasTriggeredAutoGenerate(false);
    setShowAutoGenerateButton(false);
    // Reset initial edu/exp index so next open defaults to all-closed
    setInitialEduIndex(null);
    setInitialExpIndex(null);
    setIsTypingSummary(false);
  };

  // Persist the NA (not-applicable) flag for experience or certifications
  const saveNAStatus = async (field, value) => {
    if (!candidateId) return;
    const sectionKey =
      field === 'noWorkExp' ? 'work_experience' : 'certifications';
    const dataKey = field === 'noWorkExp' ? 'no_work_exp' : 'no_certifications';
    try {
      await updateProfileSectionMutation.mutateAsync({
        candidateId,
        key: sectionKey,
        data: { [dataKey]: value },
      });
    } catch {
      toast({
        message: 'Failed to save. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const saveTitleRole = async (nextTitle) => {
    if (!effectiveCandidateId) return;
    try {
      await updateProfileSectionMutation.mutateAsync({
        candidateId: effectiveCandidateId,
        key: 'miscellaneous',
        data: { title: nextTitle },
      });
      toast({
        message: 'Title updated successfully',
        variant: 'success',
      });
    } catch {
      toast({
        message: 'Failed to save. Please try again.',
        variant: 'destructive',
      });
      throw new Error('Failed to update title');
    }
  };

  // Calculate completion progress
  const calculateProgress = () => {
    if (!resumeData) return 0;

    const shouldShowSummary = hasAnyRelevantData();

    const sections = [
      {
        key: 'contact',
        check: () => {
          // Contact section is complete if phone, email, and FULL address are present
          const hasFullAddress = !!(
            resumeData.addressLine1 &&
            resumeData.street &&
            resumeData.cityLabel &&
            resumeData.stateLabel &&
            resumeData.zipCode
          );

          return (
            resumeData.contact &&
            hasFullAddress &&
            resumeData.email &&
            emailVerified === true
          );
        },
      },
      {
        key: 'personal',
        check: () =>
          resumeData.firstname &&
          resumeData.lastname &&
          resumeData.dob &&
          resumeData.gender,
      },
      {
        key: 'education',
        check: () => areEducationsComplete(resumeData.educations),
      },
      {
        key: 'experience',
        check: () =>
          areWorkExperiencesComplete(
            resumeData.workExperiences,
            resumeData.noWorkExp
          ),
      },
      {
        key: 'skills',
        check: () => resumeData.skills?.length > 0,
      },
      {
        key: 'certifications',
        check: () => isCertificationsSectionComplete(resumeData),
      },
      {
        key: 'compensation',
        check: () =>
          resumeData.noticePeriod != null &&
          hasNoticePeriodValue(resumeData.noticePeriod) &&
          resumeData.currentCTC != null &&
          Number(resumeData.currentCTC) > 0 &&
          resumeData.expectedCTC != null &&
          Number(resumeData.expectedCTC) > 0,
      },
      {
        key: 'jobPreferences',
        check: () =>
          resumeData.preferredLocations?.length > 0 ||
          resumeData.workMode?.length > 0 ||
          resumeData.companySizes?.length > 0 ||
          resumeData.openToRelocation === true ||
          resumeData.openToRelocation === false ||
          resumeData.preferredIndustries?.length > 0,
      },
      {
        key: 'languages',
        check: () =>
          Array.isArray(resumeData.languages) &&
          resumeData.languages.length > 0,
      },
    ];

    // Only include summary when secondary + higher secondary + degree qualifications
    // exist (qualification field only), and at least one of certs, experience, or skills
    if (shouldShowSummary) {
      sections.push({
        key: 'summary',
        check: () => !!resumeData.professionalSummary,
      });
    }

    const completed = sections.filter((s) => s.check()).length;
    return Math.round((completed / sections.length) * 100);
  };

  // Summary: education tiers by qualification only, then certs / experience / skills.
  const hasAnyRelevantData = () => {
    if (!resumeData) return false;

    if (!hasRequiredEducationQualificationsForSummary(resumeData.educations))
      return false;

    const hasWorkExperiences =
      resumeData.workExperiences &&
      resumeData.workExperiences.length > 0 &&
      resumeData.workExperiences.some((exp) => exp.company || exp.role);

    const hasSkills = (resumeData.skills?.length || 0) > 0;

    const hasCertifications = (resumeData.certifications?.length || 0) > 0;

    return hasWorkExperiences || hasSkills || hasCertifications;
  };

  const progress = calculateProgress();

  // Show a 5-second completion celebration when progress first reaches 100%
  useEffect(() => {
    if (progress >= 100 && !celebrationTriggeredRef.current) {
      celebrationTriggeredRef.current = true;
      setShowCompletionCelebration(true);
      const celebrationTimer = setTimeout(() => {
        setShowCompletionCelebration(false);
      }, 5000);
      return () => clearTimeout(celebrationTimer);
    }
    if (progress < 100) {
      // Reset so it can trigger again if user removes data and re-completes
      celebrationTriggeredRef.current = false;
      setShowCompletionCelebration(false);
    }
    return undefined;
  }, [progress]);

  // Auto-generate professional summary from profile data
  const generateProfessionalSummary = (data) => {
    if (!data) return '';

    const parts = [];

    // Role/Experience
    if (data.workExperiences?.[0]?.role) {
      const exp = data.workExperiences[0];
      const duration = calculateDuration(
        exp.employment_period?.startMonth,
        exp.employment_period?.startYear,
        exp.employment_period?.endMonth,
        exp.employment_period?.endYear,
        exp.employment_period?.present
      );
      parts.push(
        `Experienced ${exp.role}${duration ? ` with ${duration} of experience` : ''}${exp.company ? ` at ${exp.company}` : ''}`
      );
    }

    // Skills
    if (data.skills?.length > 0) {
      const topSkills = (data.skills || [])
        .slice(0, 3)
        .map((s) => s.skill_name)
        .join(', ');
      if (topSkills) {
        parts.push(`Proficient in ${topSkills}`);
      }
    }

    // Education
    if (data.educations?.length > 0) {
      const degree =
        data.educations.find((e) => e.qualification === 'DEGREE') ||
        data.educations[0];
      if (degree?.qualificationLabel && degree?.institution) {
        parts.push(`${degree.qualificationLabel} from ${degree.institution}`);
      }
    }

    // Career goals (if available)
    if (data.expectedCTC || data.preferredLocations?.length > 0) {
      parts.push(
        'Seeking opportunities to contribute to innovative projects and grow professionally'
      );
    }

    return parts.join('. ') + (parts.length > 0 ? '.' : '');
  };

  // Calculate incomplete sections for motivational message
  const getIncompleteSectionsCount = () => {
    if (!resumeData) return 8; // Default to 8 (without summary)

    const shouldShowSummary = hasAnyRelevantData();

    const sections = [
      {
        key: 'contact',
        check: () => {
          // Contact section is complete if phone, email, and FULL address are present
          const hasFullAddress = !!(
            resumeData.addressLine1 &&
            resumeData.street &&
            resumeData.cityLabel &&
            resumeData.stateLabel &&
            resumeData.zipCode
          );

          return (
            resumeData.contact &&
            hasFullAddress &&
            resumeData.email &&
            emailVerified === true
          );
        },
      },
      {
        key: 'personal',
        check: () =>
          resumeData.firstname &&
          resumeData.lastname &&
          resumeData.dob &&
          resumeData.gender,
      },
      {
        key: 'education',
        check: () => areEducationsComplete(resumeData.educations),
      },
      {
        key: 'experience',
        check: () =>
          areWorkExperiencesComplete(
            resumeData.workExperiences,
            resumeData.noWorkExp
          ),
      },
      {
        key: 'skills',
        check: () => resumeData.skills?.length > 0,
      },
      {
        key: 'certifications',
        check: () => isCertificationsSectionComplete(resumeData),
      },
      {
        key: 'compensation',
        check: () =>
          resumeData.noticePeriod != null &&
          hasNoticePeriodValue(resumeData.noticePeriod) &&
          resumeData.currentCTC != null &&
          Number(resumeData.currentCTC) > 0 &&
          resumeData.expectedCTC != null &&
          Number(resumeData.expectedCTC) > 0,
      },
      {
        key: 'jobPreferences',
        check: () =>
          resumeData.preferredLocations?.length > 0 ||
          resumeData.workMode?.length > 0 ||
          resumeData.companySizes?.length > 0 ||
          resumeData.openToRelocation === true ||
          resumeData.openToRelocation === false ||
          resumeData.preferredIndustries?.length > 0,
      },
      {
        key: 'languages',
        check: () =>
          Array.isArray(resumeData.languages) &&
          resumeData.languages.length > 0,
      },
    ];

    // Only include summary when secondary + higher secondary + degree qualifications
    // exist (qualification field only), and at least one of certs, experience, or skills
    if (shouldShowSummary) {
      sections.push({
        key: 'summary',
        check: () => !!resumeData.professionalSummary,
      });
    }

    const completed = sections.filter((s) => s.check()).length;
    return sections.length - completed;
  };

  const incompleteCount = getIncompleteSectionsCount();

  // Handle resume file selection
  const handleResumeFileSelect = (event) => {
    resumeUploadHook.handleFileSelection(event);
  };

  // Handle remove file
  const handleRemoveFile = () => {
    resumeUploadHook.clearFile();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle resume upload - same flow as UploadResume.jsx
  const handleResumeUpload = async () => {
    if (!resumeUploadHook.selectedFile || !candidateId) {
      toast({
        message: 'Please select a resume file',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsUploadingResume(true);
      const phoneNumber = resumeData?.contact || '';
      const { s3Key } = await resumeUploadHook.uploadResume(
        candidateId,
        phoneNumber
      );

      if (s3Key) {
        toast({
          message: 'uploaded. Processing your profile...',
          variant: 'success',
        });

        setIsUploadingResume(false);
        setResumeOverlaySurface('processing');
        setCurrentStep(0);
        pollCountRef.current = 0;
        setProcessingPercent(getProcessingStartPercent());
        setProcessingApiLabel(null);
        uploadProcessingFirstPollDelayMsRef.current =
          UPLOAD_PROCESSING_FIRST_POLL_DELAY_AFTER_DASHBOARD_UPLOAD_MS;
        setIsUploadStatusPolling(true);
      } else {
        throw new Error('Failed to upload resume');
      }
    } catch (error) {
      toast({
        message: error.message || 'Failed to upload resume. Please try again.',
        variant: 'destructive',
      });
      setIsUploadingResume(false);
    }
  };

  // Define all sections with their metadata
  const getAllSections = () => {
    if (!resumeData) return { completed: [], incomplete: [] };

    const shouldShowSummary = hasAnyRelevantData();

    // Define sections array - ensure no duplicates by key
    const sections = [];

    // Add all sections (summary is added last, after education is complete)
    sections.push(
      {
        key: 'contact',
        title: 'Contact',
        subtitle: 'Phone, email & address',
        icon: <Globe className="w-4 h-4" />,
        isComplete: (() => {
          // Contact section is complete if phone, email, and FULL address are present
          const hasFullAddress = !!(
            resumeData.addressLine1 &&
            resumeData.street &&
            resumeData.cityLabel &&
            resumeData.stateLabel &&
            resumeData.zipCode
          );

          const result = !!(
            resumeData.contact &&
            hasFullAddress &&
            resumeData.email &&
            emailVerified === true
          );

          return result;
        })(),
        renderContent: () => (
          <div className="space-y-1">
            <FieldDisplay
              label="Phone"
              value={formatPhone(resumeData.contact)}
              icon={<Phone className="w-4 h-4" />}
            />
            <FieldDisplay
              label="Email"
              value={resumeData.email}
              icon={<Mail className="w-4 h-4" />}
              badge={
                resumeData.email ? (
                  <span
                    className={`email-verified-badge ${!emailVerified ? 'unverified' : ''}`}
                  >
                    {emailVerified ? (
                      <>
                        <CheckCircle2
                          style={{ width: 13, height: 13 }}
                          className="text-green-500"
                        />
                        Verified
                      </>
                    ) : (
                      <>
                        <XCircle
                          style={{ width: 13, height: 13 }}
                          className="text-yellow-600"
                        />
                        Unverified
                      </>
                    )}
                  </span>
                ) : null
              }
            />
            <div className="field-display">
              <div className="field-label">Address</div>
              {resumeData.addressLine1 ||
              resumeData.street ||
              resumeData.cityLabel ||
              resumeData.stateLabel ||
              resumeData.zipCode ? (
                <div className="field-value">
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      lineHeight: '1.4',
                    }}
                  >
                    {(resumeData.addressLine1 || resumeData.street) && (
                      <span>
                        {[resumeData.addressLine1, resumeData.street]
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    )}
                    {[
                      resumeData.cityLabel,
                      resumeData.stateLabel,
                      resumeData.zipCode,
                    ].filter(Boolean).length > 0 && (
                      <span className="text-xs text-gray-400">
                        {[
                          resumeData.cityLabel,
                          resumeData.stateLabel,
                          resumeData.zipCode,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="field-value">
                  <span style={{ color: 'var(--text-faint)' }}>--</span>
                </div>
              )}
            </div>
          </div>
        ),
        onEdit: () => openEditSheet('contact'),
        spanClass: 'section-span-3',
      },
      {
        key: 'compensation',
        title: 'Compensation & Availability',
        subtitle: 'Salary expectations & notice period',
        icon: (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: 16, height: 16 }}
          >
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
          </svg>
        ),
        isComplete: !!(
          resumeData.noticePeriod != null &&
          hasNoticePeriodValue(resumeData.noticePeriod) &&
          resumeData.currentCTC != null &&
          Number(resumeData.currentCTC) > 0 &&
          resumeData.expectedCTC != null &&
          Number(resumeData.expectedCTC) > 0
        ),
        renderContent: () => (
          <div style={{ paddingTop: '4px' }}>
            <div className="comp-row">
              <div className="comp-item">
                <div className="field-display">
                  <div className="field-label">Current CTC</div>
                  <div className="field-value">
                    {resumeData.currentCTC ? (
                      <>
                        <IndianRupee
                          style={{ width: '14px', height: '14px' }}
                        />
                        <span className="ctc-value">{`${(resumeData.currentCTC / 100000).toFixed(1)} LPA`}</span>
                      </>
                    ) : (
                      <span style={{ color: 'var(--text-faint)' }}>--</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="comp-item">
                <div className="field-display">
                  <div className="field-label">Expected CTC</div>
                  <div className="field-value">
                    {resumeData.expectedCTC ? (
                      <>
                        <IndianRupee
                          style={{ width: '14px', height: '14px' }}
                        />
                        <span className="ctc-value">{`${(resumeData.expectedCTC / 100000).toFixed(1)} LPA`}</span>
                      </>
                    ) : (
                      <span style={{ color: 'var(--text-faint)' }}>--</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div
              className="field-display"
              style={{
                paddingTop: '12px',
              }}
            >
              <div className="field-label">Notice Period</div>
              <div className="field-value">
                {hasNoticePeriodValue(resumeData.noticePeriod) ? (
                  <span className="notice-tag">
                    <Clock />
                    {formatNoticePeriodBadge(resumeData.noticePeriod)}
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-faint)' }}>--</span>
                )}
              </div>
            </div>
          </div>
        ),
        onEdit: () => openEditSheet('compensation'),
        spanClass: 'section-span-6',
      },
      {
        key: 'jobPreferences',
        title: 'Job Preferences',
        subtitle: 'Locations, mode, size, industries',
        icon: <MapPin className="w-4 h-4" />,
        isComplete: !!(
          resumeData.preferredLocations?.length > 0 ||
          resumeData.jobType?.length > 0 ||
          resumeData.workMode?.length > 0 ||
          resumeData.companySizes?.length > 0 ||
          resumeData.openToRelocation === true ||
          resumeData.openToRelocation === false ||
          resumeData.preferredIndustries?.length > 0
        ),
        renderContent: () => {
          const prefLocs = resumeData.preferredLocations || [];
          const masterLocs = resumeData.masterData?.preferedLocations || [];
          const jobTypes = resumeData.jobType || [];
          const workModes = resumeData.workMode || [];
          const companySizes = resumeData.companySizes || [];
          const reloc = resumeData.openToRelocation;
          const industryIds = resumeData.preferredIndustryIds || [];
          const masterIndustries = resumeData.masterData?.industries || [];

          const workModeLabel = (v) =>
            WORK_MODE_OPTIONS.find((o) => o.value === v)?.label ?? v;
          const companySizeLabel = (v) =>
            COMPANY_SIZE_OPTIONS.find((o) => o.value === v)?.label ?? v;

          return (
            <div style={{ paddingTop: 4 }}>
              {/* Row 1: 4-column grid — Location | Work Mode | Company Size | Open to Relocation */}
              <div className="jp-grid-row">
                {/* Preferred Location */}
                <div className="wp-block">
                  <div className="wp-block-label">
                    <MapPin style={{ width: 13, height: 13 }} />
                    Preferred Location
                  </div>
                  <div className="loc-pills-display">
                    {prefLocs.length > 0 ? (
                      prefLocs
                        .map((id) =>
                          masterLocs.find((l) => l.id === Number(id))
                        )
                        .filter(Boolean)
                        .map((loc) => (
                          <span key={loc.id} className="pref-pill-ro">
                            {loc.name}
                          </span>
                        ))
                    ) : (
                      <span
                        style={{
                          color: 'var(--text-faint)',
                          fontSize: '0.82rem',
                        }}
                      >
                        Not specified
                      </span>
                    )}
                  </div>
                </div>

                {/* Job Type */}
                <div className="wp-block">
                  <div className="wp-block-label">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ width: 13, height: 13 }}
                    >
                      <rect x="2" y="7" width="20" height="14" rx="2" />
                      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                    </svg>
                    Job Type
                  </div>
                  <div className="loc-pills-display">
                    {jobTypes.length > 0 ? (
                      jobTypes.map((v) => (
                        <span key={v} className="pref-pill-ro">
                          {v}
                        </span>
                      ))
                    ) : (
                      <span
                        style={{
                          color: 'var(--text-faint)',
                          fontSize: '0.82rem',
                        }}
                      >
                        Not specified
                      </span>
                    )}
                  </div>
                </div>

                {/* Work Mode */}
                <div className="wp-block">
                  <div className="wp-block-label">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ width: 13, height: 13 }}
                    >
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                    Work Mode
                  </div>
                  <div className="loc-pills-display">
                    {workModes.length > 0 ? (
                      workModes.map((v) => (
                        <span key={v} className="pref-pill-ro">
                          {workModeLabel(v)}
                        </span>
                      ))
                    ) : (
                      <span
                        style={{
                          color: 'var(--text-faint)',
                          fontSize: '0.82rem',
                        }}
                      >
                        Not specified
                      </span>
                    )}
                  </div>
                </div>

                {/* Company Size */}
                <div className="wp-block">
                  <div className="wp-block-label">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ width: 13, height: 13 }}
                    >
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                    Company Size
                  </div>
                  <div className="loc-pills-display">
                    {companySizes.length === 0 ? (
                      <span
                        style={{
                          color: 'var(--text-faint)',
                          fontSize: '0.82rem',
                        }}
                      >
                        Not specified
                      </span>
                    ) : companySizes.includes('NO_PREFERENCE') ? (
                      <span className="pref-pill-ro pref-pill-ro--neutral">
                        Open to all sizes
                      </span>
                    ) : (
                      companySizes.map((v) => (
                        <span key={v} className="pref-pill-ro">
                          {companySizeLabel(v)}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                {/* Open to Relocation */}
                <div className="wp-block">
                  <div className="wp-block-label">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ width: 13, height: 13 }}
                    >
                      <polyline points="5 9 2 12 5 15" />
                      <polyline points="19 9 22 12 19 15" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                    </svg>
                    Open to Relocation
                  </div>
                  <div style={{ paddingTop: 2 }}>
                    {reloc === true ? (
                      <span className="pref-reloc-status pref-reloc-status--yes">
                        <CheckCircle2
                          style={{ width: 15, height: 15, flexShrink: 0 }}
                        />
                        Yes — open to relocate
                      </span>
                    ) : reloc === false ? (
                      <span className="pref-reloc-status pref-reloc-status--no">
                        <XCircle
                          style={{ width: 15, height: 15, flexShrink: 0 }}
                        />
                        No — prefers stated locations
                      </span>
                    ) : (
                      <span
                        style={{
                          color: 'var(--text-faint)',
                          fontSize: '0.82rem',
                        }}
                      >
                        Not specified
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 2: Full-width — Preferred Industries */}
              <div className="wp-block jp-industries-row">
                <div className="wp-block-label">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: 13, height: 13 }}
                  >
                    <polygon points="12 2 2 7 12 12 22 7 12 2" />
                    <polyline points="2 17 12 22 22 17" />
                    <polyline points="2 12 12 17 22 12" />
                  </svg>
                  Preferred Industries
                </div>
                <div className="loc-pills-display">
                  {industryIds.length > 0 ? (
                    industryIds
                      .map((id) =>
                        masterIndustries.find((i) => i.id === Number(id))
                      )
                      .filter(Boolean)
                      .map((ind) => (
                        <span key={ind.id} className="pref-pill-ro">
                          {ind.name}
                        </span>
                      ))
                  ) : (
                    <span
                      style={{
                        color: 'var(--text-faint)',
                        fontSize: '0.82rem',
                      }}
                    >
                      Not specified
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        },
        onEdit: () => openEditSheet('jobPreferences'),
        spanClass: 'section-span-6',
      },
      {
        key: 'languages',
        title: 'Languages',
        subtitle: 'Languages you speak & proficiency',
        icon: <Globe className="w-4 h-4" />,
        isComplete: !!(
          Array.isArray(resumeData.languages) && resumeData.languages.length > 0
        ),
        renderContent: () => {
          const langs = Array.isArray(resumeData.languages)
            ? resumeData.languages
            : [];
          const sorted = [...langs].sort((a, b) => {
            if (a.isPrimary && !b.isPrimary) return -1;
            if (!a.isPrimary && b.isPrimary) return 1;
            return (a.name || '').localeCompare(b.name || '');
          });
          if (sorted.length === 0) {
            return <div className="lang-empty-ro">No languages added yet</div>;
          }
          return (
            <div className="loc-pills-display" style={{ paddingTop: 4 }}>
              {sorted.map((l, i) => (
                <span
                  key={i}
                  className={`lang-pill-ro${l.isPrimary ? ' lang-pill-ro--native' : ''}`}
                >
                  {l.isPrimary && (
                    <span
                      className="lang-pill-ro__star"
                      title="Native language"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        stroke="none"
                        style={{ width: 12, height: 12 }}
                      >
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </span>
                  )}
                  <span>{l.name}</span>
                  <span className="lang-pill-ro__sep" />
                  <span className="lang-pill-ro__prof">
                    {proficiencyLabel(l.proficiency)}
                  </span>
                </span>
              ))}
            </div>
          );
        },
        onEdit: () => openEditSheet('languages'),
        spanClass: 'section-span-6',
      },
      {
        key: 'personal',
        title: 'Personal Details',
        subtitle: 'Name, DOB & family info',
        icon: <User className="w-4 h-4" />,
        isComplete: !!(
          resumeData.firstname &&
          resumeData.lastname &&
          resumeData.dob &&
          resumeData.gender
        ),
        renderContent: () => {
          // Format gender display value - handle both lowercase and capitalized values
          const getGenderDisplay = (gender) => {
            if (!gender) return '';
            const genderStr = String(gender);
            const genderLower = genderStr.toLowerCase();
            // Map common variations to proper display format
            if (genderLower === 'male') return 'Male';
            if (genderLower === 'female') return 'Female';
            // If already in correct format, return as is
            if (['Male', 'Female'].includes(genderStr)) {
              return genderStr;
            }
            return genderStr; // Fallback to original value
          };

          const genderIcon =
            resumeData.gender?.toLowerCase() === 'female' ? '♀' : '♂';

          return (
            <div>
              <div className="field-display">
                <div className="field-label">Full Name</div>
                <div
                  className="field-value"
                  style={{
                    fontSize: '0.92rem',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                  }}
                >
                  {[resumeData.firstname, resumeData.lastname]
                    .filter(Boolean)
                    .join(' ') || '--'}
                </div>
              </div>
              <div className="personal-grid">
                <div className="field-display">
                  <div className="field-label">Gender</div>
                  <div className="field-value">
                    <span style={{ fontSize: '14px', lineHeight: 1 }}>
                      {genderIcon}
                    </span>
                    {getGenderDisplay(resumeData.gender) || '--'}
                  </div>
                </div>
                <div className="field-display">
                  <div className="field-label">Date of Birth</div>
                  <div className="field-value">
                    <CalendarDays style={{ width: 13, height: 13 }} />
                    {resumeData.dob ? formatDateHuman(resumeData.dob) : '--'}
                  </div>
                </div>
                <div className="field-display">
                  <div className="field-label">Father's Name</div>
                  <div className="field-value">
                    {resumeData.fathersname || '--'}
                  </div>
                </div>
              </div>
            </div>
          );
        },
        onEdit: () => openEditSheet('personal'),
        spanClass: 'section-span-3',
      },
      {
        key: 'experience',
        title: 'Work Experience',
        subtitle: (() => {
          // Count only work experiences with actual data (company or role)
          const validExperiences = (resumeData.workExperiences || []).filter(
            (exp) => exp.company || exp.company_name || exp.role
          );
          const count = validExperiences.length;
          return `${count} position${count !== 1 ? 's' : ''}`;
        })(),
        icon: <Briefcase className="w-4 h-4" />,
        isComplete: areWorkExperiencesComplete(
          resumeData.workExperiences,
          resumeData.noWorkExp
        ),
        renderContent: () => {
          // Check if there are any work experiences with actual data (not just empty objects)
          const hasWorkExperiences =
            resumeData.workExperiences &&
            resumeData.workExperiences.length > 0 &&
            resumeData.workExperiences.some((exp) => exp.company || exp.role);

          return (
            <div>
              {hasWorkExperiences ? (
                <div>
                  {resumeData.workExperiences
                    .map((exp, idx) => ({ exp, idx }))
                    .filter(({ exp }) => exp?.company || exp?.role)
                    .map(({ exp, idx }) => {
                      const period = exp?.employment_period || {};
                      const isPresent = !!period?.present;
                      const company = exp?.company || exp?.company_name || '';
                      const role = exp?.role || exp?.title || '';
                      const duration = calculateDuration(
                        period?.startMonth,
                        period?.startYear,
                        period?.endMonth,
                        period?.endYear,
                        isPresent
                      );

                      const fromLabel =
                        `${formatMonthFromNumeric(period?.startMonth) || ''} ${period?.startYear || ''}`.trim();
                      const toLabel = isPresent
                        ? 'Present'
                        : `${formatMonthFromNumeric(period?.endMonth) || ''} ${period?.endYear || ''}`.trim();

                      const duties = normalizeWorkExperienceDuties(exp);
                      const dutyCount = duties.length;
                      const shownDutyChips = duties.slice(0, 2);

                      return (
                        <div
                          key={`exp-row-${idx}`}
                          className="exp-row"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            openExperienceEditSheetAt(idx);
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter' && e.key !== ' ') return;
                            e.preventDefault();
                            e.stopPropagation();
                            openExperienceEditSheetAt(idx);
                          }}
                        >
                          <div
                            className={`exp-accent ${
                              isPresent
                                ? 'exp-accent--active'
                                : 'exp-accent--past'
                            }`}
                          />
                          <div className="exp-body">
                            <div className="exp-top">
                              <div className="exp-title">{role}</div>
                              {duration ? (
                                <span className="exp-duration">{duration}</span>
                              ) : null}
                            </div>
                            <div className="exp-company">
                              <Briefcase className="w-3 h-3" />
                              {company}
                            </div>
                            <div className="exp-bottom">
                              <span className="exp-dates">
                                <CalendarDays className="w-[11px] h-[11px]" />
                                {fromLabel}
                                {fromLabel && toLabel ? ' — ' : ''}
                                {toLabel}
                              </span>
                              {isPresent ? (
                                <span className="exp-present">
                                  <span className="exp-present-dot" />
                                  Present
                                </span>
                              ) : null}
                              {dutyCount > 0 ? (
                                <span className="exp-resp-count">
                                  · {dutyCount}{' '}
                                  {dutyCount === 1
                                    ? 'responsibility'
                                    : 'responsibilities'}
                                </span>
                              ) : null}
                            </div>

                            {dutyCount > 0 ? (
                              <div className="exp-duties-row">
                                {shownDutyChips.map((duty, di) => (
                                  <span
                                    key={`exp-duty-chip-${idx}-${di}`}
                                    className="exp-duty-chip"
                                    title={duty?.title || ''}
                                  >
                                    <span className="exp-duty-chip-dot" />
                                    <span className="exp-duty-chip-text">
                                      {duty?.title || 'Responsibility'}
                                    </span>
                                  </span>
                                ))}
                                {dutyCount > 2 ? (
                                  <span className="exp-duty-chip exp-duty-chip-more">
                                    +{dutyCount - 2} more
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                          </div>

                          <div className="exp-row-chevron" aria-hidden>
                            <ChevronRight
                              className="w-[14px] h-[14px]"
                              strokeWidth={2.5}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : experienceNA ? (
                <div className="cert-na-state">
                  <div className="cert-na-icon">
                    <CheckCircle2 style={{ width: 16, height: 16 }} />
                  </div>
                  <div className="cert-na-text">
                    <strong>Fresher</strong>
                    <span>No work experience yet</span>
                  </div>
                  <button
                    type="button"
                    className="cert-na-undo"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditSheet('experience');
                    }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="cert-empty-state">
                  <button
                    type="button"
                    className="cert-add-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditSheet('experience');
                    }}
                  >
                    <Plus style={{ width: 14, height: 14 }} />
                    Add Work Experience
                  </button>
                  <div className="cert-na-or">
                    <span>OR</span>
                  </div>
                  <button
                    type="button"
                    className="cert-na-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExperienceNA(true);
                      saveNAStatus('noWorkExp', true);
                    }}
                  >
                    <CheckCircle2 style={{ width: 14, height: 14 }} />
                    I'm a fresher — no experience yet
                  </button>
                </div>
              )}
            </div>
          );
        },
        onEdit: () => openEditSheet('experience'),
        spanClass: 'section-span-4',
      },
      {
        key: 'skills',
        title: 'Key Skills',
        subtitle: (() => {
          const totalSkills = resumeData.skills?.length || 0;
          return `${totalSkills} skill${totalSkills !== 1 ? 's' : ''}`;
        })(),
        icon: (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: 16, height: 16 }}
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        ),
        isComplete: !!(resumeData.skills?.length > 0),
        hideEditWhenIncomplete: false,
        renderContent: () => {
          const hasSkills = (resumeData.skills?.length || 0) > 0;

          return (
            <div>
              {hasSkills ? (
                <div className="chips">
                  {resumeData.skills.map((skill) => (
                    <span
                      key={skill.skill_id ?? skill.skill_name}
                      className="chip"
                    >
                      {skill.skill_name}
                    </span>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    flex: 1,
                    minHeight: '120px',
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditSheet('skills');
                    }}
                    className="d-empty-btn-dashed"
                  >
                    <Plus style={{ width: '14px', height: '14px' }} />
                    Add Key Skills
                  </button>
                </div>
              )}
            </div>
          );
        },
        onEdit: () => openEditSheet('skills'),
        spanClass: 'section-span-2',
      },
      {
        key: 'education',
        title: 'Education',
        subtitle: (() => {
          const count = (resumeData.educations || []).filter(
            (edu) => edu.institution || edu.qualification
          ).length;
          return `${count} qualification${count !== 1 ? 's' : ''}`;
        })(),
        icon: <GraduationCap className="w-4 h-4" />,
        isComplete: areEducationsComplete(resumeData.educations),
        hideEditWhenIncomplete: false,
        renderContent: () => {
          // Check if there are any educations with actual data (not just empty objects)
          const hasEducations = hasAnyEducationData(resumeData.educations);

          return (
            <div>
              {hasEducations ? (
                resumeData.educations.map((edu, index) => {
                  const isSec =
                    edu.qualification === 'TENTH' ||
                    edu.qualification === 'SECONDARY';
                  const isHS =
                    edu.qualification === 'TWELFTH' ||
                    edu.qualification === 'HIGHER_SECONDARY';
                  const isBachPlus = !isSec && !isHS;
                  const accentClass = isBachPlus
                    ? 'edu-accent--bach'
                    : isHS
                      ? 'edu-accent--hs'
                      : 'edu-accent--sec';
                  // Compute score display - use colors matching the accent class
                  const hasScore =
                    edu.gradeType === 'gpa'
                      ? edu.earnedGPA && edu.maxGPA
                      : edu.earnedMarks && edu.maxMarks;
                  // Adaptive score badge color based on performance threshold
                  let scoreCls = '';
                  let scoreLabel = '';
                  if (hasScore) {
                    const scored = parseFloat(
                      edu.gradeType === 'gpa' ? edu.earnedGPA : edu.earnedMarks
                    );
                    const max = parseFloat(
                      edu.gradeType === 'gpa' ? edu.maxGPA : edu.maxMarks
                    );
                    const ratio = max > 0 ? scored / max : 0;
                    if (edu.gradeType === 'gpa') {
                      scoreCls =
                        ratio >= 0.8
                          ? 'edu-score--high'
                          : ratio >= 0.6
                            ? 'edu-score--mid'
                            : 'edu-score--low';
                    } else {
                      scoreCls =
                        ratio >= 0.75
                          ? 'edu-score--high'
                          : ratio >= 0.5
                            ? 'edu-score--mid'
                            : 'edu-score--low';
                    }
                    scoreLabel =
                      edu.gradeType === 'gpa'
                        ? `GPA: ${edu.earnedGPA} / ${edu.maxGPA}`
                        : `${edu.earnedMarks} / ${edu.maxMarks}`;
                  }
                  return (
                    <div
                      key={`edu-display-${edu.institution || 'new'}-${edu.qualification || 'qual'}-${edu.graduationYear || 'year'}-${edu.board || 'board'}-${edu.university || 'uni'}`}
                      className="edu-row"
                      onClick={(e) => {
                        e.stopPropagation();
                        setInitialEduIndex(index);
                        openEditSheet('education');
                      }}
                    >
                      <div className={`edu-accent ${accentClass}`} />
                      <div className="edu-body">
                        <div className="edu-top">
                          <div>
                            <div className="edu-degree">
                              {edu.qualificationLabel || edu.qualification}
                              {edu.fieldOfStudyVal && (
                                <span className="edu-field">
                                  {' '}
                                  · {edu.fieldOfStudyVal}
                                </span>
                              )}
                            </div>
                            <div className="edu-inst">
                              <GraduationCap
                                style={{ width: '12px', height: '12px' }}
                              />
                              {edu.institution}
                              {edu.board && (
                                <span className="edu-tag">
                                  {edu.board} Board
                                </span>
                              )}
                              {edu.university &&
                                edu.university !== edu.institution && (
                                  <span className="edu-tag">
                                    {edu.university}
                                  </span>
                                )}
                            </div>
                          </div>
                          {edu.graduationYear && (
                            <span className="edu-year">
                              {edu.graduationYear}
                            </span>
                          )}
                        </div>
                        {hasScore && (
                          <div className="edu-bottom">
                            <span className={`edu-score ${scoreCls}`}>
                              <Activity
                                style={{ width: '11px', height: '11px' }}
                              />
                              {scoreLabel}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    flex: 1,
                    minHeight: '120px',
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditSheet('education');
                    }}
                    className="d-empty-btn-dashed"
                  >
                    <Plus style={{ width: '14px', height: '14px' }} />
                    Add Education
                  </button>
                </div>
              )}
            </div>
          );
        },
        onEdit: () => openEditSheet('education'),
        spanClass: 'section-span-4',
      },
      {
        key: 'certifications',
        title: 'Certifications',
        subtitle: (() => {
          const total = resumeData.certifications?.length || 0;
          return `${total} certification${total !== 1 ? 's' : ''}`;
        })(),
        icon: <Award className="w-4 h-4" />,
        isComplete: isCertificationsSectionComplete(resumeData),
        hideEditWhenIncomplete: false,
        renderContent: () => {
          const hasCertifications =
            (resumeData.certifications?.length || 0) > 0;

          return (
            <div>
              {hasCertifications ? (
                <div className="chips">
                  {resumeData.certifications.map((cert) => (
                    <span
                      key={cert.certification_id || cert.name}
                      className="chip"
                    >
                      {cert.name}
                    </span>
                  ))}
                </div>
              ) : certificationsNA ? (
                <div className="cert-na-state">
                  <div className="cert-na-icon">
                    <CheckCircle2 style={{ width: 16, height: 16 }} />
                  </div>
                  <div className="cert-na-text">
                    <span>No certifications to add</span>
                  </div>
                  <button
                    type="button"
                    className="cert-na-undo"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditSheet('certifications');
                    }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="cert-empty-state">
                  <button
                    type="button"
                    className="cert-add-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditSheet('certifications');
                    }}
                  >
                    <Plus style={{ width: 14, height: 14 }} />
                    Add Certifications
                  </button>
                  <div className="cert-na-or">
                    <span>OR</span>
                  </div>
                  <button
                    type="button"
                    className="cert-na-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCertificationsNA(true);
                      saveNAStatus('noCertifications', true);
                    }}
                  >
                    <CheckCircle2 style={{ width: 14, height: 14 }} />I don't
                    have any certifications
                  </button>
                </div>
              )}
            </div>
          );
        },
        onEdit: () => openEditSheet('certifications'),
        spanClass: 'section-span-2',
      }
    );

    // Reorder sections for 6-column desktop grid layout:
    // Row 1: contact (span-3) + personal (span-3)
    // Row 2: experience (span-4) + skills (span-2)
    // Row 3: education (span-4) + certifications (span-2)
    // Row 4: compensation (span-3) + jobPreferences (span-3)
    // Row N: summary (span-6, conditional, always last)
    const desiredOrder = [
      'contact',
      'personal',
      'experience',
      'skills',
      'education',
      'certifications',
      'compensation',
      'jobPreferences',
      'languages',
    ];
    sections.sort((a, b) => {
      const ai = desiredOrder.indexOf(a.key);
      const bi = desiredOrder.indexOf(b.key);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

    // Summary is shown first — a full-width card at the top, below the nudge
    // (matches the positions.html reference). Only when enough data exists.
    if (shouldShowSummary) {
      const summaryText = resumeData.professionalSummary;
      const summaryLen = summaryText ? summaryText.length : 0;
      const summaryQuality =
        summaryLen >= 200
          ? 'summary-quality--good'
          : summaryLen >= 100
            ? 'summary-quality--ok'
            : 'summary-quality--short';
      const summaryQualityLabel =
        summaryLen >= 200
          ? 'Great'
          : summaryLen >= 100
            ? 'Good start'
            : 'Too short';
      const eduSection = sections.find((s) => s.key === 'education');
      const isEduGated = !(eduSection && eduSection.isComplete);
      sections.unshift({
        key: 'summary',
        title: 'Professional Summary',
        subtitle: 'Brief overview of your profile',
        icon: <FileText className="w-4 h-4" />,
        isComplete: !!summaryText,
        isEduGated,
        spanClass: 'section-span-6',
        hideEditWhenIncomplete: !summaryText,
        renderContent: () => (
          <div className="space-y-4">
            {summaryText ? (
              <div className="field-display">
                <div
                  className="summary-text"
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {summaryText}
                </div>
                <div className="summary-quality-meta">
                  <span className="summary-char-count">{summaryLen} chars</span>
                  <span className={`summary-quality ${summaryQuality}`}>
                    {summaryQualityLabel}
                  </span>
                </div>
              </div>
            ) : (
              <div className="summary-empty-state">
                <p className="summary-empty-desc">
                  A strong summary highlights your role, key skills, and career
                  goals. <br />
                  Profiles with summaries receive <strong>
                    2× more views
                  </strong>{' '}
                  from recruiters.
                </p>
                <button
                  type="button"
                  className="summary-add-btn"
                  onClick={() => openEditSheet('summary')}
                >
                  + Add Summary
                </button>
              </div>
            )}
          </div>
        ),
        onEdit: () => openEditSheet('summary'),
      });
    }

    // First, deduplicate sections by key (in case of any duplicates in the array)
    const seenKeys = new Set();
    const uniqueSections = sections.filter((s) => {
      if (seenKeys.has(s.key)) {
        return false;
      }
      seenKeys.add(s.key);
      return true;
    });

    // Filter sections into completed and incomplete
    // Use strict boolean check to ensure accuracy
    const completed = uniqueSections.filter(
      (s) => Boolean(s.isComplete) === true
    );

    // CRITICAL: Ensure completed sections NEVER appear in incomplete list
    // First, create a Set of completed keys for fast lookup
    const completedKeys = new Set(completed.map((s) => s.key));

    // Filter incomplete sections: exclude any that are truly complete
    // This double-checks both the isComplete flag AND the completedKeys set
    const incomplete = uniqueSections.filter((s) => {
      // If section is in completed set, it should NOT be in incomplete
      if (completedKeys.has(s.key)) {
        return false;
      }
      // If isComplete is explicitly true, exclude it
      if (Boolean(s.isComplete) === true) {
        return false;
      }
      // Only include if truly incomplete
      return true;
    });

    // Final verification: ensure no overlap
    const incompleteKeys = new Set(incomplete.map((s) => s.key));
    const overlap = [...completedKeys].filter((key) => incompleteKeys.has(key));
    if (overlap.length > 0) {
      // Overlap detected - this should not happen
    }

    return { completed, incomplete, all: uniqueSections };
  };

  // Memoize sections to prevent unnecessary recalculations
  const {
    completed,
    incomplete,
    all: allSections,
  } = useMemo(() => {
    const result = getAllSections();

    // Final safety check: ensure no completed sections are in incomplete array
    const completedKeys = new Set(result.completed.map((s) => s.key));
    const finalIncomplete = result.incomplete.filter((s) => {
      const isInCompleted = completedKeys.has(s.key);
      if (isInCompleted) {
        return false;
      }
      // Double-check isComplete flag
      if (Boolean(s.isComplete) === true) {
        return false;
      }
      return true;
    });

    // Final verification
    const finalCompletedKeys = new Set(result.completed.map((s) => s.key));
    const finalIncompleteKeys = new Set(finalIncomplete.map((s) => s.key));
    const overlap = [...finalCompletedKeys].filter((key) =>
      finalIncompleteKeys.has(key)
    );
    if (overlap.length > 0) {
      // Overlap detected - this should not happen
    }

    return { ...result, incomplete: finalIncomplete };
  }, [resumeData, experienceNA, certificationsNA]);

  // Show green stack as soon as ≥1 section is done while others remain (not only after 2).
  const shouldShowMobileStack = completed.length >= 1 && incomplete.length > 0;

  // Generate summary text for completed sections
  const getCompletedSectionsSummary = () => {
    if (completed.length === 0) return '';
    const firstTwo = completed.slice(0, 2).map((s) => {
      const title = s.title || '';
      // Truncate long section names to keep preview compact
      if (title.length > 15) {
        return `${title.substring(0, 12)}...`;
      }
      return title;
    });
    const remaining = completed.length - 2;
    if (remaining > 0) {
      return `${firstTwo.join(', ')} +${remaining} more`;
    }
    return firstTwo.join(', ');
  };

  // Ensure mandatory education entries exist and initialize accordion state
  useEffect(() => {
    if (currentEditSection === 'education') {
      const raw = watch('educations') || [];
      const mandatoryTypes = ['TENTH', 'TWELFTH', 'DEGREE'];
      // Drop default/placeholder rows (empty qualification, no data) so we do not
      // show "New Entry" alongside Secondary / Higher Secondary / Bachelor's
      const withoutPlaceholders = raw.filter(
        (edu) =>
          !(
            isBlankEducationEntry(edu) &&
            (!edu?.qualification || String(edu.qualification).trim() === '')
          )
      );

      const existingTypes = withoutPlaceholders
        .map((edu) => edu?.qualification)
        .filter(Boolean);

      const missingTypes = mandatoryTypes.filter(
        (type) => !existingTypes.includes(type)
      );

      const newEducations = missingTypes.map((type) => ({
        ...getEmptyEducation(),
        qualification: type,
      }));

      const next = [...withoutPlaceholders, ...newEducations];

      const shouldUpdate =
        next.length !== raw.length || missingTypes.length > 0;

      if (shouldUpdate) {
        setValue('educations', next, {
          shouldValidate: false,
        });
      }
    }
  }, [currentEditSection, setValue, watch]);

  // Helper function to get education index for a qualification type
  const getEducationIndex = (qualificationType) => {
    const educations = watch('educations') || [];
    const index = educations.findIndex(
      (edu) => edu?.qualification === qualificationType
    );
    return index >= 0 ? index : -1;
  };

  // Handle save for each section
  const handleSaveSection = async () => {
    if (!resumeApi) {
      toast({
        message: 'Authentication required. Please log in to continue',
        variant: 'destructive',
      });
      return;
    }

    if (!currentEditSection) {
      toast({
        message: 'No section selected for editing',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsSaving(true);

      // Trigger react-hook-form validation for each section before API call
      let isValid = false;
      let validationFields = [];
      let errorMessage = 'Please fill in all required fields';

      switch (currentEditSection) {
        case 'contact':
          // Validate only address fields (email and contact are disabled, so skip validation)
          // Only validate address fields: addressLine1, street, state, city, zipCode
          validationFields = [
            'addressLine1',
            'street',
            'state',
            'city',
            'zipCode',
          ];
          isValid = await trigger(validationFields);
          if (!isValid) {
            // Check specific field errors for better error message
            // Note: Email and contact are disabled, so we don't validate them
            if (errors.addressLine1) {
              errorMessage =
                errors.addressLine1.message || 'Address Line 1 is required';
            } else if (errors.street) {
              errorMessage = errors.street.message || 'Street is required';
            } else if (errors.state) {
              errorMessage = errors.state.message || 'State is required';
            } else if (errors.city) {
              errorMessage = errors.city.message || 'City is required';
            } else if (errors.zipCode) {
              errorMessage = errors.zipCode.message || 'PIN code is invalid';
            } else {
              const formData = watch();
              if (!formData.addressLine1)
                errorMessage = 'Address Line 1 is required';
              else if (!formData.street) errorMessage = 'Street is required';
              else if (!formData.state) errorMessage = 'State is required';
              else if (!formData.city) errorMessage = 'City is required';
              else if (!formData.zipCode) errorMessage = 'PIN code is required';
              else errorMessage = 'Please check all contact fields';
            }
          }
          break;

        case 'personal':
          // Validate all personal detail fields (fathersname is optional)
          validationFields = ['firstname', 'lastname', 'dob', 'gender'];
          isValid = await trigger(validationFields);
          if (!isValid) {
            // Check specific field errors for better error message
            if (errors.firstname) {
              errorMessage =
                errors.firstname.message || 'First name is invalid';
            } else if (errors.lastname) {
              errorMessage = errors.lastname.message || 'Last name is invalid';
            } else if (errors.dob) {
              errorMessage = errors.dob.message || 'Date of birth is invalid';
            } else if (errors.gender) {
              errorMessage = errors.gender.message || 'Gender is required';
            } else {
              const formData = watch();
              if (!formData.firstname) errorMessage = 'First name is required';
              else if (!formData.lastname)
                errorMessage = 'Last name is required';
              else if (!formData.dob)
                errorMessage = 'Date of birth is required';
              else if (!formData.gender) errorMessage = 'Gender is required';
              else errorMessage = 'Please check all personal detail fields';
            }
          }
          break;

        case 'education':
          // Validate education entries
          isValid = await trigger('educations');
          if (!isValid) {
            setEducationValidationActive(true);
            setEducationSaveShakeKey((k) => k + 1);
            errorMessage = getEducationAttentionToastMessage(errors.educations);
          }
          break;

        case 'experience':
          // Validate work experience entries
          isValid = await trigger('workExperiences');
          if (!isValid) {
            setExperienceValidationActive(true);
            setExperienceSaveShakeKey((k) => k + 1);
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.warn('[experience-validation] save-click invalid', {
                errorsWorkExperiences: errors?.workExperiences,
                watchWorkExperiences: watch('workExperiences'),
              });
            }
            // Check for specific experience errors
            if (errors.workExperiences) {
              const expErrors = errors.workExperiences;
              const firstErrorIndex = expErrors.findIndex((err) => err);
              if (firstErrorIndex >= 0 && expErrors[firstErrorIndex]) {
                const firstError = expErrors[firstErrorIndex];
                if (firstError.role) {
                  errorMessage =
                    firstError.role.message || 'Job title is required';
                } else if (firstError.company) {
                  errorMessage =
                    firstError.company.message || 'Company is required';
                } else if (firstError.employment_period) {
                  errorMessage =
                    firstError.employment_period.message ||
                    'Employment period is invalid';
                } else {
                  errorMessage = `Please fill in all required fields for work experience entry #${firstErrorIndex + 1}`;
                }
              } else {
                errorMessage =
                  'Please fill in all required fields for work experience';
              }
            } else {
              errorMessage =
                'Please fill in all required fields for work experience';
            }
          }
          break;

        case 'compensation':
          validationFields = ['currentCTC', 'expectedCTC', 'noticePeriod'];
          isValid = await trigger(validationFields);
          if (!isValid) {
            if (errors.currentCTC) {
              errorMessage =
                errors.currentCTC.message || 'Current CTC is required';
            } else if (errors.expectedCTC) {
              errorMessage =
                errors.expectedCTC.message || 'Expected CTC is required';
            } else if (errors.noticePeriod) {
              errorMessage =
                errors.noticePeriod.message || 'Notice period is required';
            } else {
              const formData = watch();
              if (!formData.currentCTC || Number(formData.currentCTC) <= 0) {
                errorMessage = 'Current CTC is required';
              } else if (
                !formData.expectedCTC ||
                Number(formData.expectedCTC) <= 0
              ) {
                errorMessage = 'Expected CTC is required';
              } else if (
                validateNoticePeriodValue(formData.noticePeriod) !== true
              ) {
                const npErr = validateNoticePeriodValue(formData.noticePeriod);
                errorMessage =
                  typeof npErr === 'string'
                    ? npErr
                    : 'Notice period is required';
              } else {
                errorMessage = 'Please check compensation fields';
              }
            }
          }
          break;
        case 'jobPreferences':
          validationFields = ['preferredLocations'];
          isValid = await trigger(validationFields);
          if (!isValid) {
            if (errors.preferredLocations) {
              errorMessage =
                errors.preferredLocations.message ||
                'At least one preferred location is required';
            } else {
              errorMessage = 'At least one preferred location is required';
            }
          }
          break;

        case 'summary':
          // Validate summary field - check if it's null/empty first
          const summaryFormData = watch();
          const summaryValue = summaryFormData.professionalSummary;

          // Check if summary is null, undefined, or empty string
          if (
            !summaryValue ||
            (typeof summaryValue === 'string' &&
              summaryValue.trim().length === 0)
          ) {
            isValid = false;
            errorMessage = 'Please fill the required fields';
          } else {
            // Validate max length if summary exists
            isValid = await trigger('professionalSummary');
            if (!isValid) {
              // Check specific field errors for better error message
              if (errors.professionalSummary) {
                errorMessage =
                  errors.professionalSummary.message || 'Summary is invalid';
              } else if (summaryValue.length > 1000) {
                errorMessage = 'Summary must be 1000 characters or less';
              } else {
                errorMessage = 'Please check the summary field';
              }
            }
          }
          break;

        case 'skills':
          isValid = true;
          break;

        case 'certifications':
          isValid = true;
          break;

        default:
          isValid = true;
          break;
      }

      // Block API call if validation fails
      if (!isValid) {
        toast({
          message: errorMessage,
          variant: 'destructive',
        });
        setIsSaving(false);
        return;
      }

      const formData = watch();

      // Additional form-level validation - only validate the specific section being edited
      if (currentEditSection === 'education') {
        // Validate education: required qualifications and duplicates
        const reqValidation = validateRequiredQualifications(
          formData.educations || []
        );
        if (!reqValidation.isValid) {
          setEducationValidationActive(true);
          setEducationSaveShakeKey((k) => k + 1);
          toast({
            message:
              reqValidation.message ||
              'Please fill in all required education fields',
            variant: 'destructive',
          });
          setIsSaving(false);
          return;
        }

        const dupValidation = validateDuplicateSchoolQualifications(
          formData.educations || []
        );
        if (!dupValidation.isValid) {
          setEducationValidationActive(true);
          setEducationSaveShakeKey((k) => k + 1);
          toast({
            message:
              dupValidation.message || 'Duplicate education entries found',
            variant: 'destructive',
          });
          setIsSaving(false);
          return;
        }
      } else if (
        currentEditSection === 'jobPreferences' ||
        currentEditSection === 'preferences'
      ) {
        const prefValidation = validatePreferences(
          formData.preferredLocations || []
        );
        if (!prefValidation.isValid) {
          toast({
            message: 'At least one preferred location is required',
            variant: 'destructive',
          });
          setIsSaving(false);
          return;
        }
      }

      // Clearing all certifications: use same profile-section API as "I don't have certifications"
      if (
        currentEditSection === 'certifications' &&
        isCertificationsFormEmpty(formData)
      ) {
        if (!candidateId) {
          toast({
            message: 'Unable to save. Please try again.',
            variant: 'destructive',
          });
          setIsSaving(false);
          return;
        }
        try {
          await updateProfileSectionMutation.mutateAsync({
            candidateId,
            key: 'certifications',
            data: { no_certifications: true },
          });
          setCertificationsNA(true);
          toast({
            message: 'Resume updated successfully',
            variant: 'success',
          });
          closeEditSheet();
        } catch {
          toast({
            message: 'Failed to save. Please try again.',
            variant: 'destructive',
          });
        }
        return;
      }

      // Transform data for API based on section
      const payload = { ...formData };

      // Section-specific transformations
      if (currentEditSection === 'experience') {
        // Work experience uses a dedicated CRUD model (not the shared
        // section-PATCH). Diff the current form against the last-loaded server
        // snapshot:
        //  - new rows  → POST create (duties embedded)
        //  - existing  → POST update with changed fields only
        //  - removed   → DELETE by id
        // Duty edits on existing rows already fire live; any not-yet-synced
        // ones are flushed here. We refetch afterwards to reconcile ids/order.
        const filteredWorkExperiences =
          formData.workExperiences?.filter(
            (exp) =>
              exp.company ||
              exp.role ||
              exp.employment_period?.startMonth ||
              exp.employment_period?.startYear
          ) || [];

        const snapshot = resumeData?.workExperiences || [];
        const { creates, updates, deleteIds } = diffWorkExperiences(
          snapshot,
          filteredWorkExperiences
        );
        const dutyOps = collectDutyFlushOps(filteredWorkExperiences);

        // One mutation runs the whole sequence (duty flush → create/update rows
        // → delete removed rows) and invalidates the profile on success.
        await saveWorkExperiencesMutation.mutateAsync({
          dutyOps,
          experiences: [...creates, ...updates],
          deleteIds,
        });

        toast({ message: 'Resume updated successfully', variant: 'success' });
        closeEditSheet();
        return;
      }

      if (currentEditSection === 'contact') {
        // Email and contact are disabled, so preserve existing values (don't send null)
        // Only update address fields
        payload.addressLine1 = formData.addressLine1 || null;
        payload.street = formData.street || null;
        payload.state = formData.state || null;
        payload.city = formData.city || null;
        payload.zipCode = formData.zipCode || null;
        // Don't include email and contact in payload since they're disabled
      }

      if (currentEditSection === 'personal') {
        // Ensure all personal detail fields are included with null if missing
        payload.firstname = formData.firstname || null;
        payload.lastname = formData.lastname || null;
        payload.middlename = formData.middlename || null;
        payload.fathersname = formData.fathersname || null;
        payload.dob = formData.dob
          ? convertFromDateInputFormat(formData.dob)
          : null;
        payload.gender = formData.gender || null;
      }

      if (
        currentEditSection === 'preferences' ||
        currentEditSection === 'compensation' ||
        currentEditSection === 'jobPreferences'
      ) {
        // Transform compensation fields and ensure all are included with null if missing
        payload.expectedCtc = formData.expectedCTC
          ? Number(formData.expectedCTC)
          : null;
        payload.currentCtc = formData.currentCTC
          ? Number(formData.currentCTC)
          : null;
        payload.preferredWorkLocations = Array.isArray(
          formData.preferredLocations
        )
          ? formData.preferredLocations
              .map((id) => Number(id))
              .filter((id) => !Number.isNaN(id))
          : null;
        payload.noticePeriod =
          formData.noticePeriod != null && formData.noticePeriod !== ''
            ? Number(formData.noticePeriod)
            : null;
      }

      if (currentEditSection === 'jobPreferences') {
        payload.workMode = jobPrefsWorkMode;
        payload.companySizes = jobPrefsCompanySizes;
        payload.openToRelocation = jobPrefsRelocation;
        payload.jobType = jobPrefsJobTypes;
        payload.preferredIndustries = jobPrefsIndustries;
      }

      if (currentEditSection === 'languages') {
        payload.languages = langRows
          .filter((r) => r.name.trim())
          .map((r) => ({
            id: r.id,
            name: r.name.trim(),
            proficiency: r.proficiency,
            isPrimary: r.isPrimary,
          }));
      }

      if (currentEditSection === 'education') {
        payload.educations = filterNonBlankEducations(
          formData.educations || []
        );
      }

      const apiSection =
        currentEditSection === 'compensation'
          ? 'preferences'
          : currentEditSection === 'jobPreferences'
            ? 'job_preference'
            : currentEditSection;

      await updateResumeSectionMutation.mutateAsync({
        payload,
        section: apiSection,
      });

      toast({
        message: 'Resume updated successfully',
        variant: 'success',
      });

      closeEditSheet();
    } catch (error) {
      toast({
        message:
          error.message || 'Error updating resume. Please try again later',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Render edit form for current section
  const renderEditForm = () => {
    if (!currentEditSection) return null;

    switch (currentEditSection) {
      case 'contact':
        return (
          <div>
            {/* Contact details */}
            <div className="form-section form-section--prominent">
              <div className="form-section-head">
                <div className="form-section-title">Contact Details</div>
                <div className="form-section-desc">
                  How recruiters and employers will get in touch.
                </div>
              </div>

              {/* Phone — read-only */}
              <div className="fg">
                <label className="fl">Phone Number</label>
                <input
                  className="fi"
                  type="tel"
                  value={resumeData?.contact ? `+91 ${resumeData.contact}` : ''}
                  readOnly
                  style={{
                    background: 'var(--surface-muted)',
                    cursor: 'not-allowed',
                  }}
                />
              </div>

              {/* Email with inline OTP verify flow */}
              <EmailChangeSection
                currentEmail={resumeData?.email}
                initialVerified={emailVerified}
                onEmailVerified={() => {
                  refetch();
                }}
              />
            </div>

            {/* Address */}
            <div className="form-section form-section--prominent">
              <div className="form-section-head">
                <div className="form-section-title">Address</div>
                <div className="form-section-desc">
                  Used for location-based job matching and KYC.
                </div>
              </div>
              <PersonalInfoSection
                control={control}
                errors={errors}
                masterData={resumeData?.masterData || {}}
                setValue={setValue}
                hidePersonalDetails
                hideNameFields
                disableContactFields
                hideTitle
                hideContactRow
              />
            </div>
          </div>
        );
      case 'personal':
        return (
          <div>
            {/* ── Name ── */}
            <div className="form-section form-section--prominent">
              <div className="form-section-head">
                <div className="form-section-title">Name</div>
                <div className="form-section-desc">
                  Your full legal name as it appears on official documents.
                </div>
              </div>
              <div className="fg-row-3">
                <div className="fg">
                  <label className="fl">
                    First Name{' '}
                    <span style={{ color: 'var(--destructive)' }}>*</span>
                  </label>
                  <Controller
                    name="firstname"
                    control={control}
                    rules={{
                      required: 'First name is required',
                      pattern: /^[A-Za-z\s]+$/,
                    }}
                    render={({ field }) => (
                      <input
                        {...field}
                        className={`fi${errors?.firstname ? ' error' : ''}`}
                        placeholder="e.g. Shreyas"
                      />
                    )}
                  />
                  {errors?.firstname && (
                    <div className="fhint error">
                      {errors.firstname.message}
                    </div>
                  )}
                </div>
                <div className="fg">
                  <label className="fl">Middle Name</label>
                  <Controller
                    name="middlename"
                    control={control}
                    render={({ field }) => (
                      <input {...field} className="fi" placeholder="Optional" />
                    )}
                  />
                </div>
                <div className="fg">
                  <label className="fl">
                    Last Name{' '}
                    <span style={{ color: 'var(--destructive)' }}>*</span>
                  </label>
                  <Controller
                    name="lastname"
                    control={control}
                    rules={{
                      required: 'Last name is required',
                      pattern: /^[A-Za-z\s]+$/,
                    }}
                    render={({ field }) => (
                      <input
                        {...field}
                        className={`fi${errors?.lastname ? ' error' : ''}`}
                        placeholder="e.g. Kumar"
                      />
                    )}
                  />
                  {errors?.lastname && (
                    <div className="fhint error">{errors.lastname.message}</div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Personal Information ── */}
            <div className="form-section form-section--prominent">
              <div className="form-section-head">
                <div className="form-section-title">Personal Information</div>
                <div className="form-section-desc">
                  Required by some employers for KYC, age verification, and
                  background checks.
                </div>
              </div>

              {/* Gender */}
              <div className="fg">
                <label className="fl">Gender</label>
                <Controller
                  name="gender"
                  control={control}
                  rules={{ required: 'Gender is required' }}
                  render={({ field }) => (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {[
                        { value: 'Male', icon: '♂' },
                        { value: 'Female', icon: '♀' },
                      ].map(({ value, icon }) => (
                        <button
                          key={value}
                          type="button"
                          className={`gender-pill${field.value === value ? ' active' : ''}`}
                          onClick={() => field.onChange(value)}
                        >
                          {icon} {value}
                        </button>
                      ))}
                    </div>
                  )}
                />
                {errors?.gender && (
                  <div className="fhint error">{errors.gender.message}</div>
                )}
              </div>

              {/* Father's Name */}
              <div className="fg">
                <label className="fl">Father&apos;s Name</label>
                <Controller
                  name="fathersname"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="fi"
                      placeholder="Enter your father's name"
                    />
                  )}
                />
                <div className="fhint">
                  Required for background verification.
                </div>
              </div>

              {/* Date of Birth */}
              <div className="fg">
                <label className="fl">
                  Date of Birth{' '}
                  <span style={{ color: 'var(--destructive)' }}>*</span>
                </label>
                <Controller
                  name="dob"
                  control={control}
                  rules={{ required: 'Date of birth is required' }}
                  render={({ field }) => {
                    const dobValue = field.value;
                    let convertedValue = dobValue;
                    if (dobValue && /^\d{4}-\d{2}-\d{2}$/.test(dobValue)) {
                      const [year, month, day] = dobValue.split('-');
                      convertedValue = `${day}/${month}/${year}`;
                    }
                    const convertedField = {
                      ...field,
                      value: convertedValue,
                      onChange: (newValue) => {
                        if (
                          newValue &&
                          /^\d{2}\/\d{2}\/\d{4}$/.test(newValue)
                        ) {
                          const [d, m, y] = newValue.split('/');
                          field.onChange(`${y}-${m}-${d}`);
                        } else {
                          field.onChange(newValue || '');
                        }
                      },
                    };
                    return (
                      <DatePickerWithYear
                        field={convertedField}
                        error={errors?.dob}
                      />
                    );
                  }}
                />
                {errors?.dob && (
                  <div className="fhint error">{errors.dob.message}</div>
                )}
              </div>
            </div>
          </div>
        );
      case 'education':
        return (
          <EducationAccordionForm
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            resumeData={resumeData}
            getEducationIndex={getEducationIndex}
            initialOpenIndex={initialEduIndex}
            validationActive={educationValidationActive}
            validationKey={educationSaveShakeKey}
          />
        );
      case 'experience': {
        const experiences = watch('workExperiences') || [];
        return (
          <ExperienceAccordionList
            experiences={experiences}
            control={control}
            watch={watch}
            setValue={setValue}
            getValues={getValues}
            errors={errors}
            getEmptyWorkExperience={getEmptyWorkExperience}
            initialExpandedIndex={initialExpIndex}
            validationActive={experienceValidationActive}
            validationKey={experienceSaveShakeKey}
          />
        );
      }
      case 'skills':
        return (
          <SkillsSection
            control={control}
            masterData={resumeData?.masterData || {}}
            watch={watch}
          />
        );
      case 'certifications':
        return (
          <CertificationsSection
            control={control}
            masterData={resumeData?.masterData || {}}
            watch={watch}
          />
        );
      case 'preferences':
      case 'compensation':
      case 'jobPreferences':
        return (
          <div>
            {currentEditSection !== 'jobPreferences' && (
              <>
                {/* ── Salary ── */}
                <div className="form-section form-section--prominent">
                  <div className="form-section-head">
                    <div className="form-section-title">Salary</div>
                    <div className="form-section-desc">
                      Used by recruiters to match you to roles in your range.
                    </div>
                  </div>
                  <div className="fg-row">
                    <div className="fg">
                      <label className="fl">
                        Current CTC (LPA)
                        <span style={{ color: 'var(--destructive)' }}> *</span>
                      </label>
                      <Controller
                        name="currentCTC"
                        control={control}
                        rules={{
                          required: 'Current CTC is required',
                          validate: (value) => {
                            if (!value) return 'Current CTC is required';
                            const numValue =
                              typeof value === 'string'
                                ? parseFloat(value)
                                : value;
                            if (Number.isNaN(numValue) || numValue <= 0) {
                              return 'Current CTC must be greater than 0';
                            }
                            if (numValue > CTC_MAX_AMOUNT) {
                              return `Current CTC cannot exceed ${CTC_MAX_LPA} LPA`;
                            }
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <>
                            <input
                              className={`fi${errors?.currentCTC ? ' error' : ''}`}
                              type="number"
                              inputMode="decimal"
                              min={0}
                              max={CTC_MAX_LPA}
                              step="any"
                              placeholder="e.g. 10"
                              value={convertToLPA(field.value) || ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '') {
                                  field.onChange(null);
                                  return;
                                }
                                const full = convertFromLPA(v);
                                if (full !== null)
                                  field.onChange(
                                    Math.min(full, CTC_MAX_AMOUNT)
                                  );
                              }}
                              onBlur={field.onBlur}
                            />
                            {errors?.currentCTC && (
                              <div className="fhint error">
                                {errors.currentCTC.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </div>
                    <div className="fg">
                      <label className="fl">
                        Expected CTC (LPA)
                        <span style={{ color: 'var(--destructive)' }}> *</span>
                      </label>
                      <Controller
                        name="expectedCTC"
                        control={control}
                        rules={{
                          required: 'Expected CTC is required',
                          validate: (value) => {
                            if (!value) return 'Expected CTC is required';
                            const numValue =
                              typeof value === 'string'
                                ? parseFloat(value)
                                : value;
                            if (Number.isNaN(numValue) || numValue <= 0) {
                              return 'Expected CTC must be greater than 0';
                            }
                            if (numValue > CTC_MAX_AMOUNT) {
                              return `Expected CTC cannot exceed ${CTC_MAX_LPA} LPA`;
                            }
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <>
                            <input
                              className={`fi${errors?.expectedCTC ? ' error' : ''}`}
                              type="number"
                              inputMode="decimal"
                              min={0}
                              max={CTC_MAX_LPA}
                              step="any"
                              placeholder="e.g. 15"
                              value={convertToLPA(field.value) || ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '') {
                                  field.onChange(null);
                                  return;
                                }
                                const full = convertFromLPA(v);
                                if (full !== null)
                                  field.onChange(
                                    Math.min(full, CTC_MAX_AMOUNT)
                                  );
                              }}
                              onBlur={field.onBlur}
                            />
                            {errors?.expectedCTC && (
                              <div className="fhint error">
                                {errors.expectedCTC.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  {/* ── 20% hike suggestion ── */}
                  <Controller
                    name="currentCTC"
                    control={control}
                    render={({ field: currentField }) => (
                      <Controller
                        name="expectedCTC"
                        control={control}
                        render={({ field: expectedField }) => {
                          const currentLPA = parseFloat(
                            convertToLPA(currentField.value)
                          );
                          if (!currentLPA || currentLPA <= 0) return null;
                          const suggested = Math.min(
                            parseFloat((currentLPA * 1.2).toFixed(2)),
                            CTC_MAX_LPA
                          );
                          return (
                            <button
                              type="button"
                              className="ctc-suggest-btn"
                              onClick={() => {
                                const full = convertFromLPA(String(suggested));
                                if (full !== null) expectedField.onChange(full);
                              }}
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ width: 13, height: 13, flexShrink: 0 }}
                              >
                                <circle cx="12" cy="12" r="5" />
                                <line x1="12" y1="1" x2="12" y2="3" />
                                <line x1="12" y1="21" x2="12" y2="23" />
                                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                                <line
                                  x1="18.36"
                                  y1="18.36"
                                  x2="19.78"
                                  y2="19.78"
                                />
                                <line x1="1" y1="12" x2="3" y2="12" />
                                <line x1="21" y1="12" x2="23" y2="12" />
                                <line
                                  x1="4.22"
                                  y1="19.78"
                                  x2="5.64"
                                  y2="18.36"
                                />
                                <line
                                  x1="18.36"
                                  y1="5.64"
                                  x2="19.78"
                                  y2="4.22"
                                />
                              </svg>
                              Suggest: 20% hike from current ({suggested} LPA)
                            </button>
                          );
                        }}
                      />
                    )}
                  />
                </div>

                {/* ── Notice Period ── */}
                <div className="form-section form-section--prominent">
                  <div className="form-section-head">
                    <div className="form-section-title">Notice Period</div>
                    <div className="form-section-desc">
                      How soon you can start at a new role. Pick a common option
                      or enter a custom number of days.
                    </div>
                  </div>
                  <Controller
                    name="noticePeriod"
                    control={control}
                    rules={{ validate: validateNoticePeriodValue }}
                    render={({ field }) => {
                      // An unset notice period is stored as '' / null. Guard
                      // against those here: `Number('')` and `Number(null)` both
                      // coerce to 0, which would otherwise wrongly light up the
                      // "Immediate" (0) chip while the value is actually empty.
                      const hasNoticeValue =
                        field.value !== null &&
                        field.value !== undefined &&
                        field.value !== '';
                      return (
                        <>
                          <div
                            style={{
                              display: 'flex',
                              gap: 8,
                              marginBottom: 10,
                              flexWrap: 'wrap',
                            }}
                          >
                            {NOTICE_PERIOD_OPTIONS.map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                className={`preset-chip${hasNoticeValue && Number(field.value) === opt.value ? ' selected' : ''}`}
                                onClick={() => field.onChange(opt.value)}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                          {/* Show text input only for custom values (not matched by Immediate/30/60 pills) */}
                          {(field.value === null ||
                            field.value === undefined ||
                            ![0, 30, 60].includes(Number(field.value))) && (
                            <input
                              className={`fi${errors?.noticePeriod ? ' error' : ''}`}
                              type="text"
                              inputMode="numeric"
                              placeholder="Or enter custom days"
                              value={
                                field.value === null ||
                                field.value === undefined
                                  ? ''
                                  : String(field.value)
                              }
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '') {
                                  field.onChange(null);
                                  return;
                                }
                                const parsed = Number(v);
                                if (!Number.isNaN(parsed))
                                  field.onChange(parsed);
                              }}
                              onBlur={field.onBlur}
                            />
                          )}
                          {errors?.noticePeriod && (
                            <div className="fhint error">
                              {errors.noticePeriod.message}
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </div>
              </>
            )}

            {currentEditSection === 'jobPreferences' ? (
              /* ── Full Job Preferences form ── */
              <>
                {/* 1. Preferred Locations */}
                <div className="wp-block">
                  <div className="wp-block-label">
                    <MapPin style={{ width: 16, height: 16 }} />
                    Preferred Locations
                  </div>
                  <div className="wp-block-hint">
                    Cities where you&apos;d like to work. Select all that apply.
                  </div>
                  <Controller
                    name="preferredLocations"
                    control={control}
                    rules={{
                      validate: (val) =>
                        val && val.length > 0
                          ? true
                          : 'At least one location is required',
                    }}
                    render={({ field: { onChange, value } }) => {
                      const locationOptions =
                        resumeData?.masterData?.preferedLocations || [];
                      const selectedIds = (value || []).map((id) => Number(id));
                      return (
                        <>
                          <div className="loc-pills-wrap">
                            {locationOptions.map((loc) => {
                              const isSelected = selectedIds.includes(
                                Number(loc.id)
                              );
                              return (
                                <div
                                  key={loc.id}
                                  className={`loc-pill${isSelected ? ' selected' : ''}`}
                                  role="button"
                                  tabIndex={0}
                                  aria-pressed={isSelected}
                                  onClick={() => {
                                    const newIds = isSelected
                                      ? selectedIds.filter(
                                          (id) => id !== Number(loc.id)
                                        )
                                      : [...selectedIds, Number(loc.id)];
                                    onChange(newIds.map((id) => id.toString()));
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      const newIds = isSelected
                                        ? selectedIds.filter(
                                            (id) => id !== Number(loc.id)
                                          )
                                        : [...selectedIds, Number(loc.id)];
                                      onChange(
                                        newIds.map((id) => id.toString())
                                      );
                                    }
                                  }}
                                >
                                  <span className="loc-pill-check">
                                    <Check style={{ width: 12, height: 12 }} />
                                  </span>
                                  {loc.name}
                                </div>
                              );
                            })}
                          </div>
                          {errors?.preferredLocations && (
                            <div
                              className="fhint"
                              style={{
                                color: 'var(--destructive)',
                                marginTop: 4,
                              }}
                            >
                              {errors.preferredLocations.message}
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </div>

                {/* 2–5. Job Type, Work Mode, Company Size, Open to Relocation */}
                <div className="wp-grid">
                  {/* Job Type (multi-select; options from master data `jobTypes`) */}
                  <div className="wp-block">
                    <div className="wp-block-label">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ width: 16, height: 16 }}
                      >
                        <rect x="2" y="7" width="20" height="14" rx="2" />
                        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                      </svg>
                      Job Type
                    </div>
                    <div className="wp-block-hint">
                      The type of employment you&apos;re looking for. Select all
                      that apply.
                    </div>
                    <div className="loc-pills-wrap">
                      {(referenceData?.jobTypes || []).map((opt) => {
                        const isSelected = jobPrefsJobTypes.includes(opt.value);
                        return (
                          <div
                            key={opt.id}
                            className={`loc-pill${isSelected ? ' selected' : ''}`}
                            role="button"
                            tabIndex={0}
                            aria-pressed={isSelected}
                            onClick={() =>
                              setJobPrefsJobTypes((prev) =>
                                isSelected
                                  ? prev.filter((v) => v !== opt.value)
                                  : [...prev, opt.value]
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setJobPrefsJobTypes((prev) =>
                                  isSelected
                                    ? prev.filter((v) => v !== opt.value)
                                    : [...prev, opt.value]
                                );
                              }
                            }}
                          >
                            <span className="loc-pill-check">
                              <Check style={{ width: 12, height: 12 }} />
                            </span>
                            {opt.value}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Work Mode */}
                  <div className="wp-block">
                    <div className="wp-block-label">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ width: 16, height: 16 }}
                      >
                        <rect
                          x="2"
                          y="3"
                          width="20"
                          height="14"
                          rx="2"
                          ry="2"
                        />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                      </svg>
                      Work Mode
                    </div>
                    <div className="wp-block-hint">
                      Where you&apos;re open to working from. Select all that
                      apply.
                    </div>
                    <div className="loc-pills-wrap">
                      {WORK_MODE_OPTIONS.map((opt) => {
                        const isSelected = jobPrefsWorkMode.includes(opt.value);
                        return (
                          <div
                            key={opt.value}
                            className={`loc-pill${isSelected ? ' selected' : ''}`}
                            role="button"
                            tabIndex={0}
                            aria-pressed={isSelected}
                            onClick={() =>
                              setJobPrefsWorkMode((prev) =>
                                isSelected
                                  ? prev.filter((v) => v !== opt.value)
                                  : [...prev, opt.value]
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setJobPrefsWorkMode((prev) =>
                                  isSelected
                                    ? prev.filter((v) => v !== opt.value)
                                    : [...prev, opt.value]
                                );
                              }
                            }}
                          >
                            <span className="loc-pill-check">
                              <Check style={{ width: 12, height: 12 }} />
                            </span>
                            {opt.label}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Company Size */}
                  <div className="wp-block">
                    <div className="wp-block-label">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ width: 16, height: 16 }}
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                      </svg>
                      Company Size
                    </div>
                    <div className="wp-block-hint">
                      Headcount bands you&apos;d consider. &ldquo;No
                      preference&rdquo; overrides specific selections.
                    </div>
                    <div className="loc-pills-wrap">
                      {COMPANY_SIZE_OPTIONS.map((opt) => {
                        const isNoPref = opt.value === 'NO_PREFERENCE';
                        const isSelected = jobPrefsCompanySizes.includes(
                          opt.value
                        );
                        return (
                          <div
                            key={opt.value}
                            className={`loc-pill${isSelected ? ' selected' : ''}${isNoPref ? ' loc-pill--nopref' : ''}`}
                            role="button"
                            tabIndex={0}
                            aria-pressed={isSelected}
                            onClick={() => {
                              if (isNoPref) {
                                setJobPrefsCompanySizes(
                                  isSelected ? [] : ['NO_PREFERENCE']
                                );
                              } else {
                                setJobPrefsCompanySizes((prev) => {
                                  const without = prev.filter(
                                    (v) => v !== 'NO_PREFERENCE'
                                  );
                                  return isSelected
                                    ? without.filter((v) => v !== opt.value)
                                    : [...without, opt.value];
                                });
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (isNoPref) {
                                  setJobPrefsCompanySizes(
                                    isSelected ? [] : ['NO_PREFERENCE']
                                  );
                                } else {
                                  setJobPrefsCompanySizes((prev) => {
                                    const without = prev.filter(
                                      (v) => v !== 'NO_PREFERENCE'
                                    );
                                    return isSelected
                                      ? without.filter((v) => v !== opt.value)
                                      : [...without, opt.value];
                                  });
                                }
                              }
                            }}
                          >
                            <span className="loc-pill-check">
                              <Check style={{ width: 12, height: 12 }} />
                            </span>
                            {opt.label}
                            {opt.hint && (
                              <span
                                style={{
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                  opacity: 0.7,
                                  marginLeft: 4,
                                }}
                              >
                                ({opt.hint})
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Open to Relocation */}
                  <div className="wp-block">
                    <div className="wp-block-label">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ width: 16, height: 16 }}
                      >
                        <polyline points="5 9 2 12 5 15" />
                        <polyline points="19 9 22 12 19 15" />
                        <line x1="2" y1="12" x2="22" y2="12" />
                      </svg>
                      Open to Relocation
                    </div>
                    <div className="wp-block-hint">
                      Are you willing to relocate beyond your preferred
                      locations for the right role?
                    </div>
                    <div
                      className="pref-segment"
                      role="radiogroup"
                      aria-label="Open to relocation"
                    >
                      <button
                        type="button"
                        className={`pref-segment-btn pref-segment-btn--yes${jobPrefsRelocation === true ? ' selected' : ''}`}
                        aria-pressed={jobPrefsRelocation === true}
                        onClick={() => setJobPrefsRelocation(true)}
                      >
                        <CheckCircle2 style={{ width: 14, height: 14 }} />
                        Yes
                      </button>
                      <button
                        type="button"
                        className={`pref-segment-btn pref-segment-btn--no${jobPrefsRelocation === false ? ' selected' : ''}`}
                        aria-pressed={jobPrefsRelocation === false}
                        onClick={() => setJobPrefsRelocation(false)}
                      >
                        <XCircle style={{ width: 14, height: 14 }} />
                        No
                      </button>
                      <button
                        type="button"
                        className={`pref-segment-btn pref-segment-btn--null${jobPrefsRelocation === null ? ' selected' : ''}`}
                        aria-pressed={jobPrefsRelocation === null}
                        onClick={() => setJobPrefsRelocation(null)}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ width: 14, height: 14 }}
                        >
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Not specified
                      </button>
                    </div>
                  </div>
                </div>
                {/* end wp-grid */}

                {/* 5. Preferred Industries */}
                <div className="wp-block wp-block--full">
                  <div className="wp-block-label">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ width: 16, height: 16 }}
                    >
                      <polygon points="12 2 2 7 12 12 22 7 12 2" />
                      <polyline points="2 17 12 22 22 17" />
                      <polyline points="2 12 12 17 22 12" />
                    </svg>
                    Preferred Industries
                  </div>
                  <div className="wp-block-hint">
                    Tap to select; type to filter.
                  </div>

                  {/* Selected chips bar */}
                  {(() => {
                    const editMasterIndustries =
                      referenceData?.industries || [];
                    return (
                      <div
                        className={`cert-selected-bar${jobPrefsIndustries.length >= 5 ? ' scroll-mode' : ''}`}
                      >
                        {jobPrefsIndustries.length === 0 ? (
                          <span className="cert-empty-msg">
                            No industries selected
                          </span>
                        ) : (
                          jobPrefsIndustries.map((id) => {
                            const ind = editMasterIndustries.find(
                              (i) => i.id === Number(id)
                            );
                            const label = ind?.name || String(id);
                            return (
                              <span key={id} className="cert-sel-chip">
                                {label}
                                <button
                                  type="button"
                                  aria-label={`Remove ${label}`}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: 'inherit',
                                  }}
                                  onClick={() =>
                                    setJobPrefsIndustries((prev) =>
                                      prev.filter((i) => i !== id)
                                    )
                                  }
                                >
                                  <X style={{ width: 12, height: 12 }} />
                                </button>
                              </span>
                            );
                          })
                        )}
                      </div>
                    );
                  })()}

                  {/* Search input */}
                  <input
                    type="text"
                    className="cert-search"
                    placeholder="Search industries…"
                    value={jobPrefsIndustrySearch}
                    onChange={(e) => setJobPrefsIndustrySearch(e.target.value)}
                    style={{ marginTop: 8 }}
                  />

                  {/* Industry list */}
                  <div
                    style={{
                      maxHeight: 260,
                      overflowY: 'auto',
                      border: '1px solid var(--border-light)',
                      borderRadius: 'var(--radius-sm)',
                      padding: 6,
                      background: 'var(--bg)',
                      marginTop: 6,
                    }}
                  >
                    {(() => {
                      const editMasterIndustries =
                        referenceData?.industries || [];
                      const lc = jobPrefsIndustrySearch.toLowerCase();
                      const filtered = editMasterIndustries.filter(
                        (m) =>
                          m.name.toLowerCase().includes(lc) ||
                          (m.category || '').toLowerCase().includes(lc)
                      );
                      // Group by category. Industries may arrive without a
                      // category (flat { id, name } list) — bucket those under
                      // '' so no "UNDEFINED" header is rendered for them.
                      const groups = filtered.reduce((acc, m) => {
                        const cat = m.category || '';
                        if (!acc[cat]) acc[cat] = [];
                        acc[cat].push(m);
                        return acc;
                      }, {});

                      if (filtered.length === 0) {
                        return (
                          <div
                            style={{
                              padding: '8px 4px',
                              fontSize: '0.8rem',
                              color: 'var(--text-faint)',
                            }}
                          >
                            No results. Use &ldquo;Add manually&rdquo; below.
                          </div>
                        );
                      }

                      return Object.entries(groups).map(([cat, items]) => (
                        <div key={cat || 'uncategorized'}>
                          {cat ? (
                            <div className="cert-category">{cat}</div>
                          ) : null}
                          <div className="cert-list">
                            {items.map((item) => {
                              const isSelected = jobPrefsIndustries.includes(
                                item.id
                              );
                              return (
                                <div
                                  key={item.id}
                                  className={`cert-item${isSelected ? ' selected' : ''}`}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() =>
                                    setJobPrefsIndustries((prev) =>
                                      isSelected
                                        ? prev.filter((id) => id !== item.id)
                                        : [...prev, item.id]
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setJobPrefsIndustries((prev) =>
                                        isSelected
                                          ? prev.filter((id) => id !== item.id)
                                          : [...prev, item.id]
                                      );
                                    }
                                  }}
                                >
                                  <span className="cert-item-check">
                                    {isSelected && (
                                      <Check
                                        style={{ width: 10, height: 10 }}
                                      />
                                    )}
                                  </span>
                                  {item.name}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </>
            ) : currentEditSection !== 'compensation' ? (
              /* ── Preferences / combined: just Preferred Locations ── */
              <div
                className="fg"
                style={{
                  marginTop: 18,
                  paddingTop: 16,
                  borderTop: '1px solid var(--border-light)',
                }}
              >
                <label className="fl">
                  Preferred Locations
                  <span style={{ color: 'var(--destructive)' }}> *</span>
                </label>
                <div
                  className="fhint"
                  style={{ marginTop: 0, marginBottom: 8 }}
                >
                  Tap to select cities where you&apos;d like to work
                </div>
                <Controller
                  name="preferredLocations"
                  control={control}
                  rules={{
                    validate: (val) =>
                      val && val.length > 0
                        ? true
                        : 'At least one location is required',
                  }}
                  render={({ field: { onChange, value } }) => {
                    const locationOptions =
                      resumeData?.masterData?.preferedLocations || [];
                    const selectedIds = (value || []).map((id) => Number(id));
                    return (
                      <>
                        <div className="loc-pills-wrap">
                          {locationOptions.map((loc) => {
                            const isSelected = selectedIds.includes(
                              Number(loc.id)
                            );
                            return (
                              <div
                                key={loc.id}
                                className={`loc-pill${isSelected ? ' selected' : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  const newIds = isSelected
                                    ? selectedIds.filter(
                                        (id) => id !== Number(loc.id)
                                      )
                                    : [...selectedIds, Number(loc.id)];
                                  onChange(newIds.map((id) => id.toString()));
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    const newIds = isSelected
                                      ? selectedIds.filter(
                                          (id) => id !== Number(loc.id)
                                        )
                                      : [...selectedIds, Number(loc.id)];
                                    onChange(newIds.map((id) => id.toString()));
                                  }
                                }}
                              >
                                <span className="loc-pill-check">
                                  <Check className="w-[10px] h-[10px]" />
                                </span>
                                {loc.name}
                              </div>
                            );
                          })}
                        </div>
                        {errors?.preferredLocations && (
                          <div
                            className="fhint"
                            style={{
                              color: 'var(--destructive)',
                              marginTop: 4,
                            }}
                          >
                            {errors.preferredLocations.message}
                          </div>
                        )}
                      </>
                    );
                  }}
                />
              </div>
            ) : null}
          </div>
        );
      case 'languages': {
        const usedNames = langRows
          .map((r) => r.name.trim().toLowerCase())
          .filter(Boolean);
        const atMax =
          langRows.length >= (referenceData?.languages?.length || 30);

        const addLangRow = () => {
          if (atMax) return;
          setLangRows((prev) => [
            ...prev,
            {
              id: null,
              name: '',
              proficiency: 'CONVERSATIONAL',
              isPrimary: prev.length === 0,
            },
          ]);
        };

        const updateRow = (idx, patch) => {
          setLangRows((prev) =>
            prev.map((r, i) => (i === idx ? { ...r, ...patch } : r))
          );
        };

        const removeRow = (idx) => {
          setLangRows((prev) => {
            const next = prev.filter((_, i) => i !== idx);
            // If we removed the primary, promote first
            if (prev[idx].isPrimary && next.length > 0) {
              next[0] = { ...next[0], isPrimary: true };
            }
            return next;
          });
          if (langDropdownOpen === idx) setLangDropdownOpen(null);
        };

        const togglePrimary = (idx) => {
          setLangRows((prev) =>
            prev.map((r, i) => ({
              ...r,
              isPrimary: i === idx ? !r.isPrimary : false,
            }))
          );
        };

        const getDropdownOptions = (idx) => {
          const currentName = langRows[idx]?.name.trim().toLowerCase();
          const otherNames = langRows
            .filter((_, i) => i !== idx)
            .map((r) => r.name.trim().toLowerCase())
            .filter(Boolean);

          const search = langDropdownSearch.trim().toLowerCase();
          return (referenceData?.languages || []).filter(
            (l) =>
              (search === '' || l.name.toLowerCase().includes(search)) &&
              (l.name.toLowerCase() === currentName ||
                !otherNames.includes(l.name.toLowerCase()))
          );
        };

        return (
          <div className="edit-form-body" style={{ padding: '20px 16px 0' }}>
            <div className="fg" style={{ marginBottom: 0 }}>
              <label
                className="fl"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Globe style={{ width: 15, height: 15 }} />
                Languages You Speak
              </label>
              <div className="fhint" style={{ marginBottom: 8 }}>
                Select your proficiency in each language. Mark your primary
                language with the star.
              </div>

              <div className="lang-rows">
                {langRows.map((row, idx) => {
                  const isOpen = langDropdownOpen === idx;
                  const options = getDropdownOptions(idx);
                  const matched = (referenceData?.languages || []).find(
                    (l) =>
                      l.name.toLowerCase() === row.name.trim().toLowerCase()
                  );
                  const isInvalid = row.name.trim() !== '' && !matched;

                  return (
                    <div
                      key={idx}
                      className={`lang-row${isInvalid ? ' lang-row--invalid' : ''}`}
                    >
                      <div className="lang-row__lang">
                        <div
                          ref={(el) => {
                            langComboRefs.current[idx] = el;
                          }}
                          className={`lang-combo${isOpen ? ' lang-combo--open' : ''}`}
                        >
                          <input
                            className="lang-combo__input"
                            placeholder="Pick a language…"
                            value={row.name}
                            autoComplete="off"
                            spellCheck={false}
                            onChange={(e) => {
                              updateRow(idx, { name: e.target.value });
                              setLangDropdownSearch(e.target.value);
                              setLangDropdownOpen(idx);
                              if (langComboRefs.current[idx]) {
                                setLangDropdownRect(
                                  langComboRefs.current[
                                    idx
                                  ].getBoundingClientRect()
                                );
                              }
                            }}
                            onFocus={() => {
                              setLangDropdownSearch('');
                              setLangDropdownOpen(idx);
                              if (langComboRefs.current[idx]) {
                                setLangDropdownRect(
                                  langComboRefs.current[
                                    idx
                                  ].getBoundingClientRect()
                                );
                              }
                            }}
                            onBlur={() => {
                              setTimeout(() => setLangDropdownOpen(null), 150);
                            }}
                          />
                          <span
                            className="lang-combo__caret"
                            aria-hidden="true"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </span>
                          {isOpen &&
                            langDropdownRect &&
                            createPortal(
                              <div
                                className="lang-dropdown"
                                style={{
                                  top: langDropdownRect.bottom + 4,
                                  left: langDropdownRect.left,
                                  width: langDropdownRect.width,
                                }}
                              >
                                {options.length === 0 ? (
                                  <div className="lang-dropdown__empty">
                                    No languages match &ldquo;{row.name}&rdquo;
                                  </div>
                                ) : (
                                  options.map((l) => {
                                    const isCurrent =
                                      l.name.toLowerCase() ===
                                      row.name.trim().toLowerCase();
                                    const isDisabled =
                                      usedNames.includes(
                                        l.name.toLowerCase()
                                      ) && !isCurrent;
                                    return (
                                      <div
                                        key={l.id}
                                        className={`lang-dropdown__option${isCurrent ? ' is-current' : ''}${isDisabled ? ' is-disabled' : ''}`}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          if (isDisabled) return;
                                          updateRow(idx, {
                                            name: l.name,
                                            id: l.id,
                                          });
                                          setLangDropdownOpen(null);
                                        }}
                                      >
                                        {l.name}
                                        {isCurrent && (
                                          <span className="lang-dropdown__indicator">
                                            <Check
                                              style={{ width: 14, height: 14 }}
                                            />
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })
                                )}
                              </div>,
                              document.body
                            )}
                        </div>
                      </div>

                      <div className="lang-row__controls">
                        <select
                          className="lang-row__prof"
                          aria-label="Proficiency"
                          value={row.proficiency}
                          onChange={(e) =>
                            updateRow(idx, { proficiency: e.target.value })
                          }
                        >
                          {PROFICIENCY_LEVELS.map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                        <div className="lang-row__actions">
                          <button
                            type="button"
                            className={`lang-primary-btn${row.isPrimary ? ' active' : ''}`}
                            aria-pressed={row.isPrimary}
                            title="Mark as primary language"
                            onClick={() => togglePrimary(idx)}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill={row.isPrimary ? 'currentColor' : 'none'}
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ width: 16, height: 16 }}
                            >
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="lang-remove-btn"
                            aria-label="Remove language"
                            title="Remove"
                            onClick={() => removeRow(idx)}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ width: 16, height: 16 }}
                            >
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {isInvalid && (
                        <div className="lang-row__error">
                          Pick a language from the suggestions.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                className="lang-add-btn"
                disabled={atMax}
                onClick={addLangRow}
              >
                <Plus style={{ width: 14, height: 14 }} />
                Add Language
              </button>
            </div>
          </div>
        );
      }
      case 'summary': {
        const hasSummaryValue = !!resumeData?.professionalSummary;
        return (
          <div className="summary-edit-form">
            <Controller
              name="professionalSummary"
              control={control}
              rules={{
                maxLength: {
                  value: 1000,
                  message: 'Summary must be 1000 characters or less',
                },
              }}
              render={({ field }) => {
                const charCount = field.value?.length || 0;

                const handleAutoGenerate = async () => {
                  if (!resumeApi || isGeneratingSummary) return;

                  // Expand write-own section when in empty mode so textarea is visible
                  if (!hasSummaryValue) {
                    setWriteOwnExpanded(true);
                  }

                  // Clear any existing interval
                  if (typingIntervalRef.current) {
                    clearInterval(typingIntervalRef.current);
                    typingIntervalRef.current = null;
                  }

                  try {
                    setIsGeneratingSummary(true);
                    setIsTypingSummary(false);
                    setIsReadingProfileSummary(true);
                    setSummaryJustGenerated(true);
                    field.onChange(''); // Clear the field first

                    // Show "Reading profile..." phase for 2 seconds before generating summary
                    await new Promise((resolve) => {
                      setTimeout(resolve, 2000);
                    });
                    setIsReadingProfileSummary(false);

                    // Call API to get summary
                    const response = await resumeApi.getSummary();
                    const summary = response?.summary || '';

                    if (summary) {
                      // Switch to typing phase
                      setIsTypingSummary(true);
                      // Animate typing effect
                      let currentIndex = 0;
                      typingIntervalRef.current = setInterval(() => {
                        if (currentIndex < summary.length) {
                          field.onChange(
                            summary.substring(0, currentIndex + 1)
                          );
                          currentIndex++;
                        } else {
                          if (typingIntervalRef.current) {
                            clearInterval(typingIntervalRef.current);
                            typingIntervalRef.current = null;
                          }
                          setIsGeneratingSummary(false);
                          setIsTypingSummary(false);
                          setIsReadingProfileSummary(false);
                          // Keep summaryJustGenerated true so we always show filled view after generation
                          // It will be reset when the form is closed/reopened (see openEditSheet)
                        }
                      }, 5);
                    } else {
                      setIsGeneratingSummary(false);
                      setIsTypingSummary(false);
                      setIsReadingProfileSummary(false);
                      setSummaryJustGenerated(false);
                    }
                  } catch (error) {
                    if (typingIntervalRef.current) {
                      clearInterval(typingIntervalRef.current);
                      typingIntervalRef.current = null;
                    }
                    toast({
                      message: 'Failed to generate summary. Please try again.',
                      variant: 'destructive',
                    });
                    setIsGeneratingSummary(false);
                    setIsTypingSummary(false);
                    setIsReadingProfileSummary(false);
                  }
                };

                const counterColor =
                  charCount >= 200
                    ? 'var(--success)'
                    : charCount >= 100
                      ? 'var(--primary)'
                      : 'var(--text-faint)';

                // Show filled view if: saved summary exists, OR generation is in progress, OR AI has generated content, OR (user has typed content AND write-own section is not expanded)
                // Keep empty view if write-own section is expanded to prevent focus loss when typing
                // After AI generation, always show filled view (no "Let us write it for you" section)
                const showFilledView =
                  hasSummaryValue ||
                  isGeneratingSummary ||
                  summaryJustGenerated ||
                  (field.value &&
                    field.value.trim().length > 0 &&
                    !writeOwnExpanded);

                if (showFilledView) {
                  // Filled view: textarea + char counter + "Rewrite with AI" shimmer
                  return (
                    <div className="fg">
                      {isGeneratingSummary && (
                        <div
                          className="ai-generating"
                          style={
                            isTypingSummary
                              ? {
                                  background: 'var(--success-light)',
                                  color: 'var(--success)',
                                }
                              : {}
                          }
                        >
                          {isTypingSummary ? (
                            <Zap
                              style={{
                                width: '16px',
                                height: '16px',
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <div className="ai-spinner" />
                          )}
                          {isTypingSummary
                            ? 'Writing summary...'
                            : isReadingProfileSummary
                              ? 'Reading profile...'
                              : 'Generating summary...'}
                        </div>
                      )}
                      <label className="fl">Professional Summary</label>
                      <Textarea
                        {...field}
                        placeholder="Briefly describe your professional background, strengths, and career goals…"
                        className="fi"
                        rows={6}
                        maxLength={1000}
                        disabled={isGeneratingSummary}
                      />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: 6,
                        }}
                      >
                        <span className="fhint" style={{ margin: 0 }}>
                          {charCount >= 200
                            ? 'Great length!'
                            : charCount >= 100
                              ? 'Good start'
                              : '2-3 sentences is perfect'}
                        </span>
                        <span
                          style={{
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            color: counterColor,
                          }}
                        >
                          {charCount} / 1000
                        </span>
                      </div>
                      {!isGeneratingSummary && (
                        <div
                          className="autofill-shimmer"
                          onClick={handleAutoGenerate}
                          style={{
                            cursor: !resumeApi ? 'not-allowed' : 'pointer',
                            opacity: !resumeApi ? 0.5 : 1,
                          }}
                        >
                          <Sparkles
                            style={{
                              width: '13px',
                              height: '13px',
                              flexShrink: 0,
                            }}
                          />
                          <span className="shimmer-text">Rewrite with AI</span>
                        </div>
                      )}
                    </div>
                  );
                }

                // Empty view: "Let us write it for you" card + "I'll write my own" collapsible
                return (
                  <div className="fg">
                    {isGeneratingSummary && (
                      <div
                        className="ai-generating"
                        style={
                          isTypingSummary
                            ? {
                                background: 'var(--success-light)',
                                color: 'var(--success)',
                              }
                            : {}
                        }
                      >
                        {isTypingSummary ? (
                          <Zap
                            style={{
                              width: '16px',
                              height: '16px',
                              flexShrink: 0,
                            }}
                          />
                        ) : (
                          <div className="ai-spinner" />
                        )}
                        {isTypingSummary
                          ? 'Writing summary...'
                          : isReadingProfileSummary
                            ? 'Reading profile...'
                            : 'Generating summary from your profile...'}
                      </div>
                    )}
                    <div
                      style={{
                        background: 'var(--primary-light)',
                        border: '1px solid rgba(68,114,196,0.15)',
                        borderRadius: 'var(--radius-md)',
                        padding: '20px 16px',
                        marginBottom: 16,
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.88rem',
                          fontWeight: 700,
                          color: 'var(--text)',
                          marginBottom: 4,
                        }}
                      >
                        Let us write it for you
                      </div>
                      <div
                        style={{
                          fontSize: '0.76rem',
                          color: 'var(--text-muted)',
                          marginBottom: 14,
                        }}
                      >
                        We'll create a summary from your skills & experience
                      </div>
                      {!isGeneratingSummary && (
                        <button
                          type="button"
                          className="edit-btn accent"
                          onClick={handleAutoGenerate}
                          style={{
                            margin: 0,
                            width: '100%',
                            cursor: !resumeApi ? 'not-allowed' : 'pointer',
                            opacity: !resumeApi ? 0.5 : 1,
                          }}
                        >
                          <Sparkles style={{ width: '14px', height: '14px' }} />
                          Auto-Generate Summary
                        </button>
                      )}
                    </div>
                    <div
                      className={`write-own-toggle${writeOwnExpanded ? ' expanded' : ''}`}
                      onClick={() => setWriteOwnExpanded((p) => !p)}
                    >
                      <span>I'll write my own</span>
                      <ChevronDown style={{ width: '14px', height: '14px' }} />
                    </div>
                    <div
                      className={`write-own-section${writeOwnExpanded ? ' show' : ''}`}
                    >
                      <label className="fl" style={{ marginTop: 8 }}>
                        Professional Summary
                      </label>
                      <Textarea
                        {...field}
                        placeholder="I am a professional with experience in…"
                        className="fi"
                        rows={4}
                        maxLength={1000}
                        disabled={isGeneratingSummary}
                      />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: 6,
                        }}
                      >
                        <div className="fhint" style={{ margin: 0 }}>
                          2-3 sentences is perfect
                        </div>
                        <span
                          style={{
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            color: 'var(--text-faint)',
                          }}
                        >
                          {charCount} / 1000
                        </span>
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          </div>
        );
      }
      default:
        return null;
    }
  };

  if (isLoadingData || (!resumeData && !!candidateId)) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-4 text-sm text-gray-600">Loading resume data...</p>
      </div>
    );
  }

  if (!resumeData) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[60vh]">
        <p className="text-sm text-red-600">
          No resume data found. Please fill the form first.
        </p>
      </div>
    );
  }

  const hasMultipleExperiences = resumeData.workExperiences?.length > 1;

  return (
    <div className="resume-dashboard p-6">
      <ResumeProcessingOverlay
        open={resumeOverlaySurface != null}
        surface={resumeOverlaySurface ?? 'upload'}
        progressMeta={resumeProgressMeta}
        processingStep={currentStep}
        processingPercent={processingProgress}
        apiLabel={processingApiLabel}
        failedAtStep={processingFailedAtStep}
        fileInputRef={fileInputRef}
        selectedFile={resumeUploadHook.selectedFile}
        onFileInputChange={handleResumeFileSelect}
        onClearFile={handleRemoveFile}
        onUploadClick={handleResumeUpload}
        onSkipManual={() => {
          setResumeOverlaySurface(null);
          setUserSkippedResumeUpload(true);
          setProcessingApiLabel(null);
        }}
        isUploading={isUploadingResume}
        onReviewProfile={() => {
          setResumeOverlaySurface(null);
          setCurrentStep(0);
          pollCountRef.current = 0;
          setProcessingPercent(2);
          setProcessingApiLabel(null);
        }}
        onManualFill={() => {
          setResumeOverlaySurface(null);
          setUserSkippedResumeUpload(true);
          setProcessingApiLabel(null);
        }}
        onReupload={() => {
          resumeUploadHook.clearFile();
          if (fileInputRef.current) fileInputRef.current.value = '';
          setResumeOverlaySurface('upload');
          setProcessingApiLabel(null);
        }}
      />
      <div className="mx-auto max-w-[1080px] xl:max-w-[1180px] py-6 pt-0 resume-dashboard-content">
        {/* Hero Section */}
        <div className="hero-section">
          {/* Mobile Layout: Avatar and name/role side by side, badges below */}
          <div className="hero-mobile">
            <div className="hero-row">
              {/* Avatar */}
              <div className="hero-avatar">
                {resumeData.firstname?.[0]}
                {resumeData.lastname?.[0]}
              </div>

              {/* Name and Role */}
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <h2 className="hero-name" style={{ textAlign: 'left' }}>
                  {resumeData.firstname} {resumeData.lastname}
                </h2>
                <p className="hero-role" style={{ textAlign: 'left' }}>
                  <RoleInlineEdit
                    value={resumeData?.title ?? resumeData?.titleRole ?? ''}
                    placeholder="Add title"
                    inputAriaLabel="Title"
                    disabled={!effectiveCandidateId}
                    onSave={saveTitleRole}
                  />
                </p>
              </div>
            </div>

            {/* Badges - Below name/role on mobile */}
            <div className="hero-tags">
              {resumeData.stateLabel && (
                <span className="hero-tag hero-tag-location">
                  <MapPin style={{ width: '12px', height: '12px' }} />
                  {(() => {
                    const stateLabel = String(resumeData.stateLabel);
                    return (
                      stateLabel.charAt(0).toUpperCase() +
                      stateLabel.slice(1).toLowerCase()
                    );
                  })()}
                  , IN
                </span>
              )}
              {(() => {
                // Calculate total work experience: use otherDetails if available, otherwise calculate from workExperiences
                let totalMonths =
                  resumeData.otherDetails?.total_work_experience || 0;

                // If otherDetails doesn't have total_work_experience, calculate from workExperiences array
                if (!totalMonths && resumeData.workExperiences?.length > 0) {
                  totalMonths = resumeData.workExperiences.reduce(
                    (total, exp) => {
                      if (
                        !exp.employment_period?.startMonth ||
                        !exp.employment_period?.startYear
                      ) {
                        return total;
                      }
                      const start = new Date(
                        parseInt(exp.employment_period.startYear, 10),
                        parseInt(exp.employment_period.startMonth, 10) - 1,
                        1
                      );
                      const end = exp.employment_period.present
                        ? new Date()
                        : exp.employment_period.endMonth &&
                            exp.employment_period.endYear
                          ? new Date(
                              parseInt(exp.employment_period.endYear, 10),
                              parseInt(exp.employment_period.endMonth, 10) - 1,
                              1
                            )
                          : new Date();

                      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                        return total;
                      }

                      const months =
                        (end.getFullYear() - start.getFullYear()) * 12 +
                        (end.getMonth() - start.getMonth());

                      return total + Math.max(months, 0);
                    },
                    0
                  );
                }

                if (totalMonths > 0) {
                  const years = Math.floor(totalMonths / 12);
                  const months = totalMonths % 12;
                  let expText = '';
                  if (years > 0 && months > 0) {
                    expText = `${years}yr ${months}mo exp`;
                  } else if (years > 0) {
                    expText = `${years}yr exp`;
                  } else if (months > 0) {
                    expText = `${months}mo exp`;
                  }
                  return expText ? (
                    <span className="hero-tag">
                      <Briefcase style={{ width: '12px', height: '12px' }} />
                      {expText}
                    </span>
                  ) : null;
                }
                return null;
              })()}
              {hasNoticePeriodValue(resumeData.noticePeriod) && (
                <span className="hero-tag">
                  <Clock style={{ width: '12px', height: '12px' }} />
                  Notice: {formatNoticePeriodBadge(resumeData.noticePeriod)}
                </span>
              )}
            </div>
          </div>

          {/* Desktop Layout: Avatar, name/role/badges in middle, download button on right */}
          <div
            className="hidden md:flex md:flex-row md:items-center w-full"
            style={{ gap: '20px' }}
          >
            {/* Avatar */}
            <div className="hero-avatar">
              {resumeData.firstname?.[0]}
              {resumeData.lastname?.[0]}
            </div>

            {/* Name, Title, and Badges Section */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <h2 className="hero-name">
                {resumeData.firstname} {resumeData.lastname}
              </h2>
              <p className="hero-role">
                <RoleInlineEdit
                  value={resumeData?.title ?? resumeData?.titleRole ?? ''}
                  placeholder="Add title"
                  inputAriaLabel="Title"
                  disabled={!effectiveCandidateId}
                  onSave={saveTitleRole}
                />
              </p>

              {/* Badges Section - Below name on desktop */}
              <div className="hero-tags items-center">
                {resumeData.stateLabel && (
                  <span className="hero-tag hero-tag-location">
                    <MapPin style={{ width: '12px', height: '12px' }} />
                    {(() => {
                      const stateLabel = String(resumeData.stateLabel);
                      return (
                        stateLabel.charAt(0).toUpperCase() +
                        stateLabel.slice(1).toLowerCase()
                      );
                    })()}
                    , IN
                  </span>
                )}
                {(() => {
                  // Calculate total work experience: use otherDetails if available, otherwise calculate from workExperiences
                  let totalMonths =
                    resumeData.otherDetails?.total_work_experience || 0;

                  // If otherDetails doesn't have total_work_experience, calculate from workExperiences array
                  if (!totalMonths && resumeData.workExperiences?.length > 0) {
                    totalMonths = resumeData.workExperiences.reduce(
                      (total, exp) => {
                        if (
                          !exp.employment_period?.startMonth ||
                          !exp.employment_period?.startYear
                        ) {
                          return total;
                        }
                        const start = new Date(
                          parseInt(exp.employment_period.startYear, 10),
                          parseInt(exp.employment_period.startMonth, 10) - 1,
                          1
                        );
                        const end = exp.employment_period.present
                          ? new Date()
                          : exp.employment_period.endMonth &&
                              exp.employment_period.endYear
                            ? new Date(
                                parseInt(exp.employment_period.endYear, 10),
                                parseInt(exp.employment_period.endMonth, 10) -
                                  1,
                                1
                              )
                            : new Date();

                        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                          return total;
                        }

                        const months =
                          (end.getFullYear() - start.getFullYear()) * 12 +
                          (end.getMonth() - start.getMonth());

                        return total + Math.max(months, 0);
                      },
                      0
                    );
                  }

                  if (totalMonths > 0) {
                    const years = Math.floor(totalMonths / 12);
                    const months = totalMonths % 12;
                    let expText = '';
                    if (years > 0 && months > 0) {
                      expText = `${years}yr ${months}mo exp`;
                    } else if (years > 0) {
                      expText = `${years}yr exp`;
                    } else if (months > 0) {
                      expText = `${months}mo exp`;
                    }
                    return expText && expText !== '0yr 0mo exp' ? (
                      <span className="hero-tag">
                        <Briefcase style={{ width: '12px', height: '12px' }} />
                        {expText}
                      </span>
                    ) : (
                      ''
                    );
                  }
                  return '';
                })()}
                {hasNoticePeriodValue(resumeData.noticePeriod) && (
                  <span className="hero-tag">
                    <Clock style={{ width: '12px', height: '12px' }} />
                    Notice: {formatNoticePeriodBadge(resumeData.noticePeriod)}
                  </span>
                )}
                <span className="flex items-center justify-center gap-1 text-[0.7rem] text-[var(--text-faint)] font-medium whitespace-nowrap">
                  <RotateCcw style={{ width: '11px', height: '11px' }} />
                  Updated{' '}
                  {resumeData.updatedAt
                    ? formatRelativeTime(resumeData.updatedAt)
                    : ''}
                </span>
              </div>
            </div>

            {/* Resume download moved to the topbar header (see setHeaderEndContent) */}
          </div>
        </div>

        {/* Skill-boost CTA — shown when skills detected from the resume still
            await review. Whole card links to the skill-details page. */}
        {unconfirmedSkills.show && (
          <Link
            to="/my-skills"
            className="pd-skill-boost"
            aria-label="Update your skill details to rank higher in recruiter searches"
          >
            <span className="pd-skill-boost-icon">
              <Sparkles />
            </span>
            <span className="pd-skill-boost-body">
              <strong>
                Update your skill details
                {unconfirmedSkills.count > 0 && (
                  <span className="pd-skill-boost-count">
                    {unconfirmedSkills.count}
                  </span>
                )}
              </strong>
              <span className="pd-skill-boost-sub">
                {unconfirmedSkills.count > 0
                  ? `${unconfirmedSkills.count} skill${
                      unconfirmedSkills.count === 1 ? '' : 's'
                    } detected from your experience — review and rate them to rank higher in recruiter searches.`
                  : 'Review and rate your skills to rank higher in recruiter searches.'}
              </span>
            </span>
            <span className="pd-skill-boost-cta">
              <span className="pd-skill-boost-cta-text">Update skills</span>
              <ChevronRight />
            </span>
          </Link>
        )}

        {/* Header with Progress */}
        {(progress < 100 || showCompletionCelebration) && (
          <div
            className={`progress-banner${showCompletionCelebration ? ' completing' : ''}`}
          >
            {showCompletionCelebration ? (
              /* Celebration state */
              <div className="progress-banner-content">
                <div className="circular-progress-wrapper">
                  <svg className="circular-progress" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="var(--success-light)"
                      strokeWidth="4"
                    />
                    <circle
                      className="circular-progress-fill"
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="var(--success)"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 45}`}
                      strokeDashoffset="0"
                      style={{ transition: 'stroke-dashoffset 0.6s ease-out' }}
                    />
                  </svg>
                  <div
                    className="circular-progress-text"
                    style={{ color: 'var(--success)', fontSize: '0.65rem' }}
                  >
                    100%
                  </div>
                </div>
                <div className="progress-banner-text">
                  <div className="progress-banner-title completion-title">
                    {/* <Sparkles
                      style={{
                        display: 'inline',
                        width: '15px',
                        height: '15px',
                        marginRight: '5px',
                        verticalAlign: 'middle',
                        color: '#419677',
                      }}
                    /> */}
                    Profile Complete
                  </div>
                  <div className="progress-banner-subtitle completion-subtitle">
                    Your profile is 100% complete.
                  </div>
                </div>
              </div>
            ) : (
              /* pd-nudge style */
              <div
                className="progress-banner-content"
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  incomplete.length > 0 && openEditSheet(incomplete[0].key)
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (incomplete.length > 0) openEditSheet(incomplete[0].key);
                  }
                }}
              >
                {/* Small ring */}
                <div className="progress-nudge-ring">
                  <svg viewBox="0 0 40 40">
                    <circle
                      cx="20"
                      cy="20"
                      r="16"
                      fill="none"
                      stroke="rgba(68,114,196,0.12)"
                      strokeWidth="3"
                    />
                    <circle
                      cx="20"
                      cy="20"
                      r="16"
                      fill="none"
                      stroke="var(--primary)"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 16}`}
                      strokeDashoffset={`${2 * Math.PI * 16 * (1 - progress / 100)}`}
                      style={{
                        transition:
                          'stroke-dashoffset 0.8s cubic-bezier(0.16,1,0.3,1)',
                        transform: 'rotate(-90deg)',
                        transformOrigin: '20px 20px',
                      }}
                    />
                    <text
                      x="20"
                      y="20"
                      dominantBaseline="central"
                      textAnchor="middle"
                      style={{
                        fill: 'var(--primary)',
                        fontSize: '9px',
                        fontWeight: 800,
                      }}
                    >
                      {progress}%
                    </text>
                  </svg>
                </div>
                {/* Text */}
                <div className="progress-banner-text">
                  <strong className="progress-nudge-title">
                    {incompleteCount} section{incompleteCount !== 1 ? 's' : ''}{' '}
                    left
                  </strong>
                  <span className="progress-nudge-sub">
                    Complete your profile to boost visibility
                  </span>
                </div>
                {/* Chevron */}
                <ChevronRight
                  style={{
                    width: '14px',
                    height: '14px',
                    color: 'var(--text-faint)',
                    flexShrink: 0,
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Accordion Sections - Mobile: Incomplete first, then CompletedStack below */}
        {/* Mobile: Incomplete cards OR all cards when all completed (as regular accordions) */}
        <div
          className={`sections-mobile md:hidden${openMobileCard ? ' has-open' : ''}`}
          id="sectionsList"
        >
          {(() => {
            if (incomplete.length === 0) {
              const byKey = Object.fromEntries(
                (allSections || []).map((s) => [s.key, s])
              );

              const toggleGroup = (groupId, sectionKeys) => {
                setExpandedGroupId((prev) =>
                  prev === groupId ? null : groupId
                );
                // Collapse any open accordions inside groups when switching
                setOpenMobileCard(null);
                setOpenSections((prev) => {
                  const next = { ...prev };
                  sectionKeys.forEach((k) => {
                    next[k] = false;
                  });
                  return next;
                });
              };

              const personalKeys = ['contact', 'personal'];
              const profKeys = ['summary', 'skills', 'certifications'];
              const prefKeys = ['compensation', 'jobPreferences'];

              const personalSubtitle = personalKeys
                .map((k) => byKey[k]?.title)
                .filter(Boolean)
                .join(', ');
              const profSubtitle = profKeys
                .map((k) => byKey[k]?.title)
                .filter(Boolean)
                .join(', ');
              const prefSubtitle = prefKeys
                .map((k) => byKey[k]?.title)
                .filter(Boolean)
                .join(', ');

              const renderSectionCard = (section) => {
                if (!section) return null;
                const isOpen =
                  openMobileCard === section.key || openSections[section.key];
                return (
                  <div key={`group-${section.key}`}>
                    <AccordionCard
                      title={section.title}
                      subtitle={section.subtitle}
                      icon={section.icon}
                      isComplete={section.isComplete}
                      isOpen={isOpen}
                      onToggle={() => toggleSection(section.key)}
                      onEdit={section.onEdit}
                      showEdit
                      hideEditWhenIncomplete={!!section.hideEditWhenIncomplete}
                      className={[
                        section.key === 'experience' &&
                          'accordion-card-experience',
                        section.key === 'education' &&
                          'accordion-card-education',
                        (section.key === 'preferences' ||
                          section.key === 'compensation' ||
                          section.key === 'jobPreferences') &&
                          'accordion-card-preferences',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {section.renderContent()}
                    </AccordionCard>
                  </div>
                );
              };

              return (
                <div
                  className={`sections-mobile all-complete${openMobileCard || Object.values(openSections).some(Boolean) ? ' has-open' : ''}`}
                >
                  <SectionGroup
                    id="group-personal-info"
                    title="Personal Information"
                    iconClassName="gi-personal"
                    icon={<User style={{ width: 16, height: 16 }} />}
                    subtitle={personalSubtitle}
                    count={personalKeys.length}
                    expanded={expandedGroupId === 'personal-info'}
                    hasOpenCard={personalKeys.some(
                      (k) => openMobileCard === k || openSections[k]
                    )}
                    onToggle={() => toggleGroup('personal-info', personalKeys)}
                  >
                    {personalKeys.map((k) => renderSectionCard(byKey[k]))}
                  </SectionGroup>

                  <SectionGroup
                    id="group-professional-profile"
                    title="Professional Profile"
                    iconClassName="gi-professional"
                    icon={<FileText style={{ width: 16, height: 16 }} />}
                    subtitle={profSubtitle}
                    count={profKeys.length}
                    expanded={expandedGroupId === 'professional-profile'}
                    hasOpenCard={profKeys.some(
                      (k) => openMobileCard === k || openSections[k]
                    )}
                    onToggle={() =>
                      toggleGroup('professional-profile', profKeys)
                    }
                  >
                    {profKeys.map((k) => renderSectionCard(byKey[k]))}
                  </SectionGroup>

                  <SectionGroup
                    id="group-job-preferences"
                    title="Job Preferences"
                    iconClassName="gi-preferences"
                    icon={<IndianRupee style={{ width: 16, height: 16 }} />}
                    subtitle={prefSubtitle}
                    count={prefKeys.length}
                    expanded={expandedGroupId === 'job-preferences'}
                    hasOpenCard={prefKeys.some(
                      (k) => openMobileCard === k || openSections[k]
                    )}
                    onToggle={() => toggleGroup('job-preferences', prefKeys)}
                  >
                    {prefKeys.map((k) => renderSectionCard(byKey[k]))}
                  </SectionGroup>

                  {/* Individual (ungrouped) sections come after all groups */}
                  {renderSectionCard(byKey.experience)}
                  {renderSectionCard(byKey.education)}
                  {renderSectionCard(byKey.languages)}
                </div>
              );
            }

            // When stack is visible, only show incomplete sections (completed are in stack)
            // Otherwise (all complete OR all incomplete), show all sections as regular accordions
            const sectionsToRender = shouldShowMobileStack
              ? incomplete // Only show incomplete when stack handles the completed ones
              : allSections; // Show all when there's no stack

            return sectionsToRender.map((section, i) => {
              const isOpen =
                openMobileCard === section.key || openSections[section.key];

              return (
                <div
                  key={`mobile-${section.key}`}
                  className={section.spanClass || ''}
                  style={{ animationDelay: `${0.06 + i * 0.04}s` }}
                >
                  <AccordionCard
                    title={section.title}
                    subtitle={section.subtitle}
                    icon={section.icon}
                    isComplete={section.isComplete}
                    isOpen={isOpen}
                    onToggle={() => {
                      // For summary section when missing, open edit modal directly instead of expanding
                      if (section.key === 'summary' && !section.isComplete) {
                        section.onEdit();
                        return;
                      }
                      toggleSection(section.key);
                    }}
                    onEdit={section.onEdit}
                    showEdit
                    hideEditWhenIncomplete={!!section.hideEditWhenIncomplete}
                    className={[
                      section.key === 'experience' &&
                        'accordion-card-experience',
                      section.key === 'education' && 'accordion-card-education',
                      (section.key === 'preferences' ||
                        section.key === 'compensation' ||
                        section.key === 'jobPreferences') &&
                        'accordion-card-preferences',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {section.renderContent()}
                  </AccordionCard>
                </div>
              );
            });
          })()}

          {/* Mobile: Completed Stack — footer lives inside stack-cards (stack-footer) per reference HTML */}
          {shouldShowMobileStack && (
            <>
              <div className="section-divider">
                <span className="section-divider-text">
                  <Check className="w-3.5 h-3.5" />
                  {completed.length} section
                  {completed.length !== 1 ? 's' : ''} completed
                </span>
                <div className="section-divider-line" />
              </div>
              <CompletedStack
                completedCards={completed}
                openCardKey={openMobileCard}
                onToggle={toggleMobileCard}
                renderCardContent={renderCardContent}
                onEdit={openEditSheet}
                stackFooter={
                  <footer className="app-footer stack-footer">
                    <div className="app-footer-links">
                      <a href="/terms">Terms &amp; Conditions</a>
                      <span className="dot">•</span>
                      <a href="/privacy">Privacy Policy</a>
                    </div>
                    <div className="app-footer-copy">
                      &copy; 2026 Ahedge. All rights reserved.
                    </div>
                  </footer>
                }
              />
            </>
          )}

          {/* When no completed stack: footer at end of #sectionsList (sections-footer), per reference HTML */}
          {!shouldShowMobileStack && (
            <footer className="app-footer sections-footer">
              <div className="app-footer-links">
                <a href="/terms">Terms &amp; Conditions</a>
                <span className="dot">•</span>
                <a href="/privacy">Privacy Policy</a>
              </div>
              <div className="app-footer-copy">
                &copy; 2026 Ahedge. All rights reserved.
              </div>
            </footer>
          )}
        </div>

        {/* Desktop: Grid Layout (All Sections - Including Summary) */}
        {/* NOTE: Only render on desktop to prevent duplicate sections on mobile */}
        {/* CRITICAL: This renders ALL sections (completed + incomplete) - only visible on desktop */}
        {isDesktop && allSections.length > 0 && (
          <div className="grid sections-grid" data-view="desktop-all-sections">
            {allSections.map((section) => (
              <div
                key={`desktop-${section.key}`}
                className={section.spanClass || ''}
                data-render-source="desktop-grid"
                data-section-key={section.key}
                data-edu-gated={section.isEduGated ? 'true' : undefined}
              >
                <AccordionCard
                  title={section.title}
                  subtitle={section.subtitle}
                  icon={section.icon}
                  isComplete={section.isComplete}
                  isOpen={openSections[section.key]}
                  onToggle={() => toggleSection(section.key)}
                  onEdit={section.onEdit}
                  showEdit
                  hideEditWhenIncomplete={!!section.hideEditWhenIncomplete}
                  editButtonText={
                    section.key === 'summary' ? 'Edit' : undefined
                  }
                  className={[
                    section.key === 'summary' && !resumeData.professionalSummary
                      ? 'summary-hide-edit'
                      : '',
                    section.key === 'experience' && 'accordion-card-experience',
                    section.key === 'education' && 'accordion-card-education',
                    (section.key === 'preferences' ||
                      section.key === 'compensation' ||
                      section.key === 'jobPreferences') &&
                      'accordion-card-preferences',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {section.renderContent()}
                </AccordionCard>
              </div>
            ))}
          </div>
        )}

        <footer className="app-footer hidden md:block">
          <div className="app-footer-links">
            <a href="/terms">Terms &amp; Conditions</a>
            <span className="dot">•</span>
            <a href="/privacy">Privacy Policy</a>
          </div>
          <div className="app-footer-copy">
            &copy; 2026 Ahedge. All rights reserved.
          </div>
        </footer>
      </div>

      {/* Mobile Sticky CTA — complete remaining sections */}
      <div className="md:hidden">
        {incomplete.length > 0 && !editSheetOpen && (
          <div className="sticky-nudge">
            <button
              className="sticky-nudge-btn"
              onClick={() => openEditSheet(incomplete[0].key)}
            >
              <FileText
                style={{ width: '18px', height: '18px', flexShrink: 0 }}
              />
              Complete {incompleteCount} Remaining Section
              {incompleteCount !== 1 ? 's' : ''}
            </button>
            <div className="sticky-nudge-hint">
              Profiles with summaries get 2× more views
            </div>
          </div>
        )}
      </div>

      {/* Edit Sheet */}
      <EditSheet
        isOpen={editSheetOpen}
        onClose={closeEditSheet}
        title={
          currentEditSection === 'summary'
            ? 'Professional Summary'
            : currentEditSection === 'compensation'
              ? 'Compensation & Availability'
              : currentEditSection === 'jobPreferences'
                ? 'Job Preferences'
                : currentEditSection === 'languages'
                  ? 'Languages'
                  : currentEditSection
                    ? `Edit ${currentEditSection.charAt(0).toUpperCase() + currentEditSection.slice(1)}`
                    : 'Edit'
        }
        onSave={handleSaveSection}
        isLoading={isSaving}
        section={currentEditSection}
        saveShakeKey={
          currentEditSection === 'education'
            ? educationSaveShakeKey
            : currentEditSection === 'experience'
              ? experienceSaveShakeKey
              : 0
        }
        educationEntryCount={educationEntryCount}
      >
        {renderEditForm()}
      </EditSheet>

      {/* Sticky bottom CTA — mobile only, visible when profile is incomplete */}
      {incomplete.length > 0 && (
        <div className="sticky-cta">
          <button
            type="button"
            className="sticky-cta-btn"
            onClick={() => openEditSheet(incomplete[0].key)}
          >
            <ChevronRight style={{ width: '18px', height: '18px' }} />
            <span className="sticky-cta-label">
              Complete Your {incomplete[0].title}
            </span>
          </button>
          <p className="sticky-cta-sub">
            Profiles with summaries get 2× more views
          </p>
        </div>
      )}
    </div>
  );
}
